import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  UnitLoggedOnPopup,
  CallAssignedPopup,
  UpdatePopup,
  StatusConfirmPopup,
  LogoffConfirmPopup,
} from "@/components/crew/MdtPopups";
import { CommandPrompt } from "@/components/crew/CommandPrompt";
import { useLocationReporter, GpsShareIndicator } from "@/components/gps/useLocationReporter";
import { LogonForm } from "@/components/crew/LogonForm";
import { FunctionKeys } from "@/components/crew/FunctionKeys";
import { MessagesCanvas } from "@/components/crew/MessagesCanvas";
import { MdtErrorBoundary } from "@/components/crew/MdtErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGNAL_LABELS = {
  63: "ENROUTE TO CALL",
  84: "ON SCENE OF CALL",
  82: "TRANSPORTING TO HOSPITAL",
  81: "AT HOSPITAL",
  97: "AVAILABLE WITHIN RESPONSE AREA",
  98: "AVAILABLE OUTSIDE RESPONSE AREA",
  99: "AVAILABLE AT HEADQUARTERS",
  89: "AVAILABLE AT CROSS STREET LOCATION",
};

const AVAILABLE_SIGNALS = [97, 98, 99, 89, 0];
const CALL_SIGNALS = [10, 63, 84, 82, 81];

export default function CrewLoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [agency, setAgency] = useState("");
  const [agencyData, setAgencyData] = useState(null);
  const [unitId, setUnitId] = useState("");
  const [unitData, setUnitData] = useState(null);
  const [boroughCoverage, setBoroughCoverage] = useState("");
  const [tour, setTour] = useState("");
  const [crew1, setCrew1] = useState("");
  const [crew2, setCrew2] = useState("");
  const [crew3, setCrew3] = useState("");
  const [crew4, setCrew4] = useState("");
  const [radio1, setRadio1] = useState("");
  const [radio2, setRadio2] = useState("");
  const [radio3, setRadio3] = useState("");
  const [radio4, setRadio4] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commandInput, setCommandInput] = useState("");
  // ─── MDT Command Prompt ───
  // System-sourced "messages" generated from command output (UI/UH/ETR/ASG/HNR).
  // These render in the MSG LIST canvas alongside real messages but are never
  // persisted to the backend — they exist only for the current session.
  const [systemMessages, setSystemMessages] = useState([]);
  // Bottom-bar transient message (replaces "no more messages"). Set by the
  // denial path of SUPV-only commands to flash "I can't let you do that!".
  const [bottomMessage, setBottomMessage] = useState("");

  // `forceLogonForm` is set TRUE when the crew taps LOGON while already logged
  // on. It overrides `loggedIn` and makes the CADLOGON screen render again so
  // the crew can re-submit with swapped/updated crew members or radios. The
  // flag is cleared automatically after a successful re-logon.
  const [forceLogonForm, setForceLogonForm] = useState(false);

  // Post-login state - restore from localStorage if available
  const [loggedIn, setLoggedIn] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).loggedIn || false : false;
  });
  const [showLoggedInPopup, setShowLoggedInPopup] = useState(false);
  const [showStatusPopup, setShowStatusPopup] = useState(null);
  const [currentSignal, setCurrentSignal] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).currentSignal || null : null;
  });
  const [signalTimestamp, setSignalTimestamp] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).signalTimestamp || null : null;
  });
  // Track all signal timestamps (63, 84, 82, 81 persist even after moving forward)
  const [signalTimestamps, setSignalTimestamps] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).signalTimestamps || {} : {};
  });
  const [statusChangedAt, setStatusChangedAt] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).statusChangedAt || null : null;
  });
  const [logonData, setLogonData] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).logonData || null : null;
  });
  const [onCall, setOnCall] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).onCall || false : false;
  });
  const [showDispoPanel, setShowDispoPanel] = useState(false);
  const [extendedStatus, setExtendedStatus] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).extendedStatus || null : null;
  }); // e.g. "EXTENDED PAPERWORK" for 81A/81S/81T
  // Tracks which hospital-transport key was pressed: "HA" or "HB" (for separate F-key timestamps)
  const [hospitalChoice, setHospitalChoice] = useState(() => {
    const saved = localStorage.getItem("crew_session");
    return saved ? JSON.parse(saved).hospitalChoice || null : null;
  });

  // ─── Message inbox state ───
  // Crew receives messages from dispatchers + supervisors. UI:
  //   • MESSAGE WAITING (sidebar) — RED + count when ≥1 unread; click to
  //     open the messages canvas mode and auto-display the OLDEST unread
  //     message, mark it read. Per operator spec the WAITING button is the
  //     ONLY way to advance through unread messages — PREV MSG steps to the
  //     previous one in the inbox.
  //   • MSG LIST (top tab) — switches the canvas to messages mode. The
  //     small list panel renders on the RIGHT half; the selected message's
  //     full body renders on the LEFT half, formatted exactly like the
  //     reference photo: MM/DD/YY HH:MM:SS  To <unit> from <sender_id>:
  //     followed by the body. REPLY + DELETE buttons live UNDER the list.
  //     Reply collapses the message and shows two fields (To: + body) with
  //     a SEND button. Reply only works for SUPV units. SUPV units also get
  //     a NEW MSG button to compose a fresh broadcast (To: dropdown of all
  //     online entities + SEND ALL).
  const [inboxMessages, setInboxMessages] = useState([]);            // sent + received, not soft-deleted
  // showMessages: false | "list" | "waiting"
  //   "list"    → MSG LIST canvas (LEFT detail + RIGHT history panel)
  //   "waiting" → MESSAGE WAITING canvas (LEFT-only filtered to UNREAD only,
  //               no RIGHT history panel — per operator spec the WAITING
  //               button must NEVER trigger the message list panel)
  const [showMessages, setShowMessages] = useState(false);           // canvas mode toggle
  const [selectedMessageId, setSelectedMessageId] = useState(null);  // currently shown on left
  const [composeMode, setComposeMode] = useState(null);              // null | "reply" | "new"
  const [composeTo, setComposeTo] = useState("");                    // for compose dropdown
  const [composeBody, setComposeBody] = useState("");
  const messageDingRef = useRef(null);
  const seenMessageIdsRef = useRef(new Set());
  // Roster of currently-logged-in dispatchers + units, used by the SUPV
  // NEW MSG composer's To: dropdown. Refreshed each time we open the MDT
  // messages canvas + every 30s while it's open.
  const composeRosterRef = useRef([]);
  const [composeRoster, setComposeRoster] = useState([]);

  // Lazy-init the ding audio so the first new-message event plays a clean tone.
  useEffect(() => {
    if (!messageDingRef.current) {
      try {
        messageDingRef.current = new Audio("/audio/message_ding.mp3");
        messageDingRef.current.volume = 1.0;
      } catch {}
    }
  }, []);

  const playMessageDing = useCallback(() => {
    try {
      if (messageDingRef.current) {
        messageDingRef.current.currentTime = 0;
        const p = messageDingRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } catch {}
  }, []);

  // Iter 117 — MDT shares its own location to native GPS once a crew is logged on.
  const mdtGpsUnit = loggedIn && logonData?.unit_id ? logonData.unit_id : "";
  const mdtGps = useLocationReporter(mdtGpsUnit ? `${API}/gps/mdt/${mdtGpsUnit}` : "", !!mdtGpsUnit);

  // Fetch full message history any time the unit logs on / changes.
  const fetchMessages = useCallback(async () => {
    if (!logonData?.unit_id) return;
    try {
      const r = await axios.get(`${API}/messages`, { params: { user_type: "unit", user_id: logonData.unit_id } });
      const msgs = r.data || [];
      setInboxMessages(msgs);
      // Pre-seed seen set from already-read messages so a refresh doesn't re-ding.
      msgs.filter((m) => m.read_at).forEach((m) => seenMessageIdsRef.current.add(m.id));
    } catch {}
  }, [logonData]);

  // Restore form fields from session
  useEffect(() => {
    const saved = localStorage.getItem("crew_session");
    if (saved) {
      const s = JSON.parse(saved);
      if (s.agency) setAgency(s.agency);
      if (s.unitId) setUnitId(s.unitId);
    }
  }, []);

  // ─── On-mount: Server-truth session restore (hard-reset / reboot safe) ───
  // When the MDT boots, localStorage gives us a SNAPSHOT of the last known
  // session — but the server is the source of truth. We must:
  //   1. Verify the unit still has logged_in=true on the backend (operator
  //      may have force-logged-off this unit while the MDT was powered down).
  //   2. Refresh signal + status_changed_at from the server so the on-screen
  //      timer reflects TRUE elapsed time, not the snapshot from localStorage
  //      (which could be hours stale after a long power outage).
  //   3. Re-hydrate assignedCall from the unit's current_job_id.
  //
  // If the server reports logged_in=false (or the unit no longer exists),
  // clear localStorage and let the clean login screen render — operator
  // chose option (b) "show clean login like nothing happened".
  useEffect(() => {
    if (!loggedIn || !logonData?.unit_id) return;
    let cancelled = false;
    (async () => {
      try {
        const unitsRes = await axios.get(`${API}/units`);
        const u = (unitsRes.data || []).find((x) => x.id === logonData.unit_id);

        // Unit was force-logged-off (or deleted) while the MDT was offline.
        // Per operator spec: clear local snapshot and drop to the clean
        // login screen so the crew can re-logon if/when needed.
        if (!u || !u.logged_in) {
          if (cancelled) return;
          localStorage.removeItem("crew_session");
          setLoggedIn(false);
          setLogonData(null);
          setCurrentSignal(null);
          setSignalTimestamp(null);
          setSignalTimestamps({});
          setStatusChangedAt(null);
          setOnCall(false);
          setExtendedStatus(null);
          setHospitalChoice(null);
          setAssignedCall(null);
          return;
        }

        // Unit still in service — refresh authoritative state from server.
        // The on-screen duration is derived from statusChangedAt (a wall-clock
        // ms timestamp); pulling the server value here means the timer
        // reflects the real elapsed time since the last status change, even
        // after a multi-hour power outage.
        if (cancelled) return;
        if (u.signal !== undefined && u.signal !== null) {
          setCurrentSignal(u.signal);
        }
        if (u.status_changed_at) {
          const tsMs = new Date(u.status_changed_at).getTime();
          if (!Number.isNaN(tsMs)) setStatusChangedAt(tsMs);
          const tsLabel = new Date(u.status_changed_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
          setSignalTimestamp(tsLabel);
        }
        // CALL signals (10 dispatched / 63 enroute / 84 on-scene / 82 transporting / 81 at-hospital).
        // Triggers the call-info panel to render and the F-key chrome to show on-call styling.
        const CALL_SET = new Set([10, 63, 84, 82, 81]);
        setOnCall(CALL_SET.has(u.signal));

        if (assignedCall) return;  // already populated (e.g. live WS dispatch)
        if (!u.current_job_id) return;

        const jobRes = await axios.get(`${API}/jobs/${u.current_job_id}`);
        if (cancelled) return;
        if (!jobRes.data || jobRes.data.error) return;
        // Look up call_code + segment via /api/calltypes so the call sheet
        // header renders identically to the live WS path. Without this the
        // call code field is blank after F5.
        let callCode = "";
        let segment = "";
        const callTypeFull = jobRes.data.call_type || "";
        try {
          const ctRes = await axios.get(`${API}/calltypes`);
          const ct = (ctRes.data || []).find((x) => x.call_type === jobRes.data.call_type);
          if (ct) {
            callCode = ct.call_code || "";
            segment = ct.segment || "";
          }
        } catch { /* fall through with empty call_code */ }
        if (cancelled) return;
        // Reconstruct the assignedCall envelope matching the WS shape so the
        // rest of the MDT renders unchanged (call info, status footer, etc.).
        setAssignedCall({
          type: "job_assigned",
          unit_id: u.id,
          unit_name: u.name,
          unit: u,
          job: jobRes.data,
          call_code: callCode,
          segment,
          call_type_full: callTypeFull,
        });
        setOnCall(true);
        // Auto-open CALL INFO so the crew lands on the call sheet after
        // refresh — matches operator spec "CAD info should remain intact".
        setShowCallInfo(true);
      } catch { /* network blip — try again on next WS sync */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, logonData?.unit_id]);

  // Save session to localStorage whenever key state changes
  useEffect(() => {
    if (loggedIn && logonData) {
      localStorage.setItem("crew_session", JSON.stringify({
        loggedIn, currentSignal, signalTimestamp, signalTimestamps, statusChangedAt, logonData, onCall,
        agency, unitId, extendedStatus, hospitalChoice,
      }));
    }
  }, [loggedIn, currentSignal, signalTimestamp, signalTimestamps, statusChangedAt, logonData, onCall, agency, unitId, extendedStatus, hospitalChoice]);

  // HA/HB Hospital transport popup state
  const [showHospitalPopup, setShowHospitalPopup] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  // Active diversions — fetched alongside hospitals so the CCC popup can
  // skip a hospital that's currently on diversion for a category and pick
  // the next-closest accepting one instead.
  const [activeDiversions, setActiveDiversions] = useState([]);
  // Current ambulance precinct/atom — looked up from this unit's most
  // recent GPS coord. Used in the 10-82/10-81 status footer.
  const [unitAtom, setUnitAtom] = useState("");
  const [hospitalCategory, setHospitalCategory] = useState("");
  const [hospitalOverride, setHospitalOverride] = useState("");
  const [hospitalCloser, setHospitalCloser] = useState("");
  const [hospitalPatientReq, setHospitalPatientReq] = useState("");
  const [hospitalSupv, setHospitalSupv] = useState("");
  const [hospitalOlmc, setHospitalOlmc] = useState("");
  const [hospitalAltDest, setHospitalAltDest] = useState("");
  const [closestHospital, setClosestHospital] = useState("");

  // Call assignment state (from WebSocket)
  const [assignedCall, setAssignedCall] = useState(null);
  const [showCallAssignedPopup, setShowCallAssignedPopup] = useState(false);
  const [showCallInfo, setShowCallInfo] = useState(false);
  const [showMciList, setShowMciList] = useState(false);
  // Orange CALL INFO button when dispatcher adds new call_info / location_data
  const [hasUnseenInfo, setHasUnseenInfo] = useState(false);
  // Yellow "UPDATE" popup shown when a dispatcher changes the call's address
  const [addressUpdate, setAddressUpdate] = useState(null); // { new_address, old_address, timestamp }

  // Fetch hospitals for HA/HB popup
  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data)).catch(() => {});
    // Initial diversions fetch + light polling (refresh every 30s so newly
    // posted/expired diversions reflect on the CCC screen without a reload).
    const fetchDiversions = () => axios.get(`${API}/diversions`, { params: { active_only: true } })
      .then((r) => setActiveDiversions(r.data || []))
      .catch(() => setActiveDiversions([]));
    fetchDiversions();
    const divTimer = setInterval(fetchDiversions, 30000);
    return () => clearInterval(divTimer);
  }, []);

  // Unit History state
  const [showUnitHistory, setShowUnitHistory] = useState(false);
  const [unitHistory, setUnitHistory] = useState([]);

  // Logoff confirmation state
  const [showLogoffConfirm, setShowLogoffConfirm] = useState(false);

  // Duration ticker
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);

  // ─── CALL-ASSIGNED audio alert ───
  // When a NEW CALL ASSIGNED popup appears (for primary OR assist), play the
  // emergency_pending.mp3 tone to its full duration, pause 2 seconds, play
  // again, and repeat until the crew clicks the popup to acknowledge.
  const popupAudioRef = useRef(null);
  const popupAudioGapRef = useRef(null);
  useEffect(() => {
    // Lazily create one Audio instance reused across triggers.
    if (!popupAudioRef.current) {
      const a = new Audio("/audio/emergency_pending.mp3");
      a.preload = "auto";
      popupAudioRef.current = a;
    }
    const a = popupAudioRef.current;
    if (!a) return;

    const clearGap = () => {
      if (popupAudioGapRef.current) {
        clearTimeout(popupAudioGapRef.current);
        popupAudioGapRef.current = null;
      }
    };

    if (showCallAssignedPopup) {
      // Start playing immediately; on end, wait 2s then replay.
      const onEnded = () => {
        clearGap();
        popupAudioGapRef.current = setTimeout(() => {
          // If popup is still visible when the 2s elapses, loop again.
          if (popupAudioRef.current) {
            try { popupAudioRef.current.currentTime = 0; popupAudioRef.current.play().catch(() => {}); } catch {}
          }
        }, 2000);
      };
      a.addEventListener("ended", onEnded);
      // Kick it off.
      try { a.currentTime = 0; a.play().catch(() => {}); } catch {}
      return () => {
        a.removeEventListener("ended", onEnded);
        clearGap();
      };
    } else {
      // Popup dismissed — stop immediately.
      try { a.pause(); a.currentTime = 0; } catch {}
      clearGap();
    }
  }, [showCallAssignedPopup]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/agencies`),
      axios.get(`${API}/units`),
      axios.get(`${API}/users`),
    ]).then(([a, u, usr]) => {
      // Iter 108 — alphabetize by last name so crew dropdowns are easy
      // to scan on a small MDT screen.
      const sortedUsers = [...usr.data].sort((a, b) => {
        const al = (a.last_name || "").toLowerCase();
        const bl = (b.last_name || "").toLowerCase();
        if (al !== bl) return al.localeCompare(bl);
        return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());
      });
      setAgencies(a.data); setAllUnits(u.data); setUsers(sortedUsers);
    }).catch(() => {});
  }, []);

  // ─── Iter 83: EMS terminal auto-resolve via MAC/DEV/IP ───
  // Each terminal carries three identifiers:
  //   • A persistent device fingerprint (UUID4) generated once and saved
  //     in localStorage. Sent as `X-Client-Dev` on every request via an
  //     axios interceptor below.
  //   • An optional manual MAC the operator types during a one-time
  //     "device setup" prompt. Stored in localStorage, sent as
  //     `X-Client-Mac`.
  //   • The client's IP, read server-side.
  // The backend matches any of those against `unit.mac_addresses[]` and
  // returns the bound unit. If matched we skip the unit dropdown and
  // pre-select the unit. If not, we show identifiers + a manual override.
  const [emsBindState, setEmsBindState] = useState({
    loading: true,
    bound: false,
    boundUnit: null,
    identifiers: { ip: "", mac: "", dev: "" },
    showManualOverride: false,
    showMacPrompt: false,
  });

  // One-shot: ensure DEV fingerprint exists + axios sends both identifier
  // headers on every request (including the data fetch above on next mount,
  // and every subsequent API call from this terminal).
  useEffect(() => {
    let dev = "";
    try {
      dev = localStorage.getItem("ems_mdt_device_id") || "";
      if (!dev) {
        // Browser-safe UUID generator (crypto.randomUUID falls back gracefully).
        dev = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        localStorage.setItem("ems_mdt_device_id", dev);
      }
    } catch { /* private mode etc. — fall through to no-DEV */ }
    let savedMac = "";
    try { savedMac = localStorage.getItem("ems_mdt_mac") || ""; } catch {}

    // Attach as default headers on the global axios instance. New requests
    // pick this up immediately; in-flight requests are not affected.
    if (dev) axios.defaults.headers.common["X-Client-Dev"] = dev;
    if (savedMac) axios.defaults.headers.common["X-Client-Mac"] = savedMac;

    (async () => {
      try {
        const r = await axios.post(`${API}/ems-mdt/identify`);
        const matched = r.data?.unit;
        setEmsBindState({
          loading: false,
          bound: !!r.data?.bound,
          boundUnit: matched || null,
          identifiers: r.data?.identifiers || { ip: "", mac: savedMac, dev },
          showManualOverride: false,
          showMacPrompt: false,
        });
        // Auto-fill the unit + agency on the logon form if we got a match
        // and the operator hasn't already typed something into the form.
        if (matched && matched.id && !unitId) {
          setUnitId(matched.id);
          if (matched.agency && !agency) setAgency(matched.agency);
        }
      } catch {
        setEmsBindState((s) => ({ ...s, loading: false, identifiers: { ip: "", mac: savedMac, dev } }));
      }
    })();
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTerminalMac = (rawMac) => {
    const normalized = String(rawMac || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
    if (normalized.length !== 12) return false;
    const canonical = normalized.match(/.{1,2}/g).join(":");
    try { localStorage.setItem("ems_mdt_mac", canonical); } catch {}
    axios.defaults.headers.common["X-Client-Mac"] = canonical;
    setEmsBindState((s) => ({
      ...s,
      identifiers: { ...s.identifiers, mac: canonical },
      showMacPrompt: false,
    }));
    // Re-run identify with the new header.
    axios.post(`${API}/ems-mdt/identify`).then((r) => {
      const matched = r.data?.unit;
      setEmsBindState((s) => ({
        ...s,
        bound: !!r.data?.bound,
        boundUnit: matched || null,
        identifiers: r.data?.identifiers || s.identifiers,
      }));
      if (matched && matched.id && !unitId) {
        setUnitId(matched.id);
        if (matched.agency) setAgency(matched.agency);
      }
    }).catch(() => {});
    return true;
  };

  const manualBindThisTerminal = async (targetUnitId) => {
    if (!targetUnitId) return;
    try {
      const r = await axios.post(`${API}/ems-mdt/bind`, { unit_id: targetUnitId });
      if (r.data?.error) return;
      const matched = r.data?.unit;
      setEmsBindState((s) => ({
        ...s,
        bound: true,
        boundUnit: matched || null,
        showManualOverride: false,
      }));
      if (matched) {
        setUnitId(matched.id);
        if (matched.agency) setAgency(matched.agency);
      }
    } catch { /* ignored — operator can retry */ }
  };

  // ─── Iter 88: keep the bound unit in sync with state ───
  // Race condition that caused "MISSING REQUIRED FIELDS: UNIT" even though the
  // visible Unit label showed the bound unit:
  //   1. /ems-mdt/identify resolves before /api/units finishes loading.
  //   2. We setUnitId(matched.id) and setAgency(matched.agency).
  //   3. The agency-watcher effect runs with allUnits=[] (still loading) and
  //      its guard `allUnits.find(...)` returns undefined → it wipes unitId.
  //   4. Later, allUnits loads but unitId is now "" and the guard short-
  //      circuits on `if (!prev) return prev;` — no recovery.
  // Result: unitData stays null, isSupervisor stays false (so form demands
  // 2 crew + 2 radios for SUPVs), XMIT fails with "UNIT missing".
  //
  // Fix: dedicated re-sync effect that fires whenever allUnits OR the bound
  // unit changes. If we have a binding and the unit exists in the loaded
  // list, force unitId + agency to match.
  useEffect(() => {
    if (!emsBindState?.bound || !emsBindState.boundUnit?.id) return;
    if (!allUnits || allUnits.length === 0) return;
    const boundId = emsBindState.boundUnit.id;
    const found = allUnits.find((u) => u.id === boundId);
    if (!found) return; // bound to a unit that no longer exists — leave alone
    setUnitId((prev) => (prev === boundId ? prev : boundId));
    if (found.agency) setAgency((prev) => (prev === found.agency ? prev : found.agency));
  }, [emsBindState?.bound, emsBindState?.boundUnit?.id, allUnits]);

  useEffect(() => {
    if (!agency) { setAgencyData(null); setBoroughCoverage(""); return; }
    const ag = agencies.find((a) => a.name === agency);
    setAgencyData(ag || null);
    const bc = ag?.borough_coverage || "";
    const parts = bc.split(",").map(b => b.trim()).filter(Boolean);
    setBoroughCoverage(parts.length === 1 ? parts[0] : ""); // auto-set if only one, else crew picks
    // Iter 85 — when agency changes, only clear the unit/crew/radio fields
    // if the currently-selected unit doesn't belong to the new agency. This
    // preserves the MAC-binding auto-fill: identify sets agency + unitId in
    // parallel, and without this guard the agency effect would race in and
    // wipe unitId, leaving the LOGON form silently un-submittable. If the
    // dispatcher manually picks a different agency the unit no longer
    // belongs to, we still clear as before.
    setUnitId((prev) => {
      if (!prev) return prev;
      // Iter 88 — if allUnits hasn't loaded yet, keep the current unitId
      // unchanged. Wiping while data is still in-flight caused the bound
      // unit to disappear permanently (the re-sync effect below catches
      // this case once the data arrives).
      if (!allUnits || allUnits.length === 0) return prev;
      const cur = allUnits.find((u) => u.id === prev);
      return cur && cur.agency === agency ? prev : "";
    });
    setUnitData((prev) => {
      if (!prev) return prev;
      if (!allUnits || allUnits.length === 0) return prev;
      return prev.agency === agency ? prev : null;
    });
    // Crew / radio don't auto-fill from MAC binding, so a true agency change
    // still warrants clearing them. We only skip when the agency effect is
    // running because MAC binding picked the same agency the unit is in
    // (which means crew/radio fields are still empty anyway).
    setCrew1(""); setCrew2(""); setCrew3(""); setCrew4("");
    setRadio1(""); setRadio2(""); setRadio3(""); setRadio4("");
  }, [agency, agencies, allUnits]);

  useEffect(() => {
    if (!unitId) { setUnitData(null); return; }
    const u = allUnits.find((un) => un.id === unitId);
    setUnitData(u || null);
  }, [unitId, allUnits]);

  // ─── Supervisor borough auto-pick ───
  // Supervisors cover the entire agency. Per operator spec: when a SUPV
  // unit is selected, automatically place them in their agency's first
  // borough (or the only one, if the agency covers a single borough).
  // The crew never picks the field — it's hidden in the form for SUPVs.
  // For non-SUPV units the original "auto-set if single, else crew picks"
  // logic still runs from the agency-change effect above.
  useEffect(() => {
    if (!unitData) return;
    if ((unitData.unit_type || "").toUpperCase() !== "SUPERVISOR") return;
    const ag = agencies.find((a) => a.name === agency);
    const parts = ((ag?.borough_coverage || "")
      .split(",").map((b) => b.trim()).filter(Boolean));
    if (parts.length > 0) setBoroughCoverage(parts[0]);
  }, [unitData, agency, agencies]);

  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const dateStr = now.toLocaleDateString("en-US", { weekday: "short" }) + " " +
    String(now.getMonth() + 1).padStart(2, "0") + "/" +
    String(now.getDate()).padStart(2, "0") + "/" +
    now.getFullYear() + " " +
    String(now.getHours()).padStart(2, "0") + ":" +
    String(now.getMinutes()).padStart(2, "0");

  const getMilTime = () => {
    const d = new Date();
    return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
  };

  const getMilTimeFull = () => {
    const d = new Date();
    return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0") + ":" + String(d.getSeconds()).padStart(2, "0");
  };

  // Borough abbreviation
  // Format address for crew display: upper-case, strip state/country,
  // convert city → borough abbreviation, insert comma before borough.
  //   "1221 Fulton Street APT 4K Brooklyn" → "1221 FULTON STREET APT 4K, BK"
  //   "Stuyvesant Avenue and Macon Street, Brooklyn" → "STUYVESANT AVENUE AND MACON STREET, BK"
  const abbreviateBorough = (address) => {
    if (!address) return "";
    let s = String(address).trim();
    // Strip trailing state/country.
    s = s.replace(/,\s*(NY|USA|US|UNITED STATES(?: OF AMERICA)?)\s*$/i, "");
    s = s.replace(/\s*,\s*/g, ", ").trim();
    const map = { "BROOKLYN": "BK", "QUEENS": "QN", "BRONX": "BX", "NEW YORK": "MN", "MANHATTAN": "MN", "STATEN ISLAND": "SI" };
    const upper = s.toUpperCase();
    for (const [full, abbr] of Object.entries(map)) {
      // Prefer boundary-matched comma split first (clean replacement)
      const commaRe = new RegExp(`,\\s*${full}$`, "i");
      if (commaRe.test(s)) return s.replace(commaRe, `, ${abbr}`).toUpperCase();
      // Else match trailing space-separated borough and insert a comma.
      const tailRe = new RegExp(`\\s+${full}$`, "i");
      if (tailRe.test(s)) return s.replace(tailRe, `, ${abbr}`).toUpperCase();
      if (upper.includes(full)) return s.replace(new RegExp(full, "i"), abbr).toUpperCase();
    }
    return s.toUpperCase();
  };

  // WebSocket for receiving job assignments - use ref for stable reference
  const logonDataRef = useRef(logonData);
  // Mirror of assignedCall as a ref so the WS handler can compare prev.job.id
  // without using a functional updater (functional updaters must be pure —
  // calling other setStates inside them breaks under StrictMode).
  const assignedCallRef = useRef(assignedCall);
  const currentSignalRef = useRef(currentSignal);
  const hospitalChoiceRef = useRef(hospitalChoice);
  useEffect(() => { logonDataRef.current = logonData; }, [logonData]);
  useEffect(() => { assignedCallRef.current = assignedCall; }, [assignedCall]);
  useEffect(() => { currentSignalRef.current = currentSignal; }, [currentSignal]);
  useEffect(() => { hospitalChoiceRef.current = hospitalChoice; }, [hospitalChoice]);

  useEffect(() => {
    if (!loggedIn || !logonData) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://") + "/api/ws";
    let ws = null;
    let reconnectTimer = null;
    let heartbeatInterval = null;
    let pongTimeout = null;

    const clearAllTimers = () => {
      if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null; }
      if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        console.log("Crew WS connected");
        // Heartbeat: ping every 25s, arm a 10s pong timeout per ping.
        // K8s ingress may close idle sockets silently — the ping keeps the
        // link alive AND the pong timeout detects zombie sockets (open in
        // browser state but actually dead) so we force-reconnect.
        heartbeatInterval = setInterval(() => {
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          try {
            ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
            if (pongTimeout) clearTimeout(pongTimeout);
            pongTimeout = setTimeout(() => {
              try { ws.close(); } catch {}
            }, 10000);
          } catch {}
        }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          // Pong → connection is healthy, cancel zombie timeout.
          if (msg.type === "pong") {
            if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; }
            return;
          }
          // ─── Roster / unit / agency refresh events ───
          // These fire on CADLOGON BEFORE the unit is logged in, so they must
          // run *before* the `if (!ld) return` guard below. When the dispatcher
          // updates users, units, or agencies (create/update/delete, bulk
          // Excel upload, authorized-crew edit), the MDT's CADLOGON dropdowns
          // refresh in real time with no manual reload.
          if (msg.type === "users_changed") {
            axios.get(`${API}/users`).then((r) => setUsers([...r.data].sort((a, b) => {
              const al = (a.last_name || "").toLowerCase();
              const bl = (b.last_name || "").toLowerCase();
              if (al !== bl) return al.localeCompare(bl);
              return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());
            }))).catch(() => {});
            return;
          }
          if (msg.type === "unit_updated" && msg.unit) {
            setAllUnits((prev) => {
              const found = prev.some((u) => u.id === msg.unit.id);
              return found
                ? prev.map((u) => (u.id === msg.unit.id ? msg.unit : u))
                : [...prev, msg.unit];
            });
          }
          const ld = logonDataRef.current;
          if (!ld) return;
          if (msg.type === "job_assigned" && msg.unit_id === ld.unit_id) {
            // Guard: only show the CALL ASSIGNED popup ONCE per unit per job.
            // Subsequent `job_assigned` messages for the same job (e.g. WS
            // reconnect replay, or dispatcher re-broadcasts) update the
            // assignedCall but do not re-open the popup.
            //
            // CRITICAL: do NOT call other setStates from inside a functional
            // updater callback — React requires updater functions to be pure
            // and may double-invoke them under StrictMode, causing missed
            // setShow* calls. Read prev via a ref outside the callback.
            const prevAssigned = assignedCallRef.current;
            const alreadyOnThisJob = prevAssigned?.job?.id && msg.job?.id && prevAssigned.job.id === msg.job.id;
            setAssignedCall(msg);
            if (!alreadyOnThisJob) {
              setShowCallAssignedPopup(true);
              // Close every secondary canvas — the popup must dominate.
              setShowCallInfo(false);
              setShowUnitHistory(false);
              setShowDispoPanel(false);
              setShowHospitalPopup(false);
              setShowMessages(false);
              setShowMciList(false);
              setShowScratchPad(false);
              setComposeMode(null);
              setOnCall(true);
            }
            // Defensive PCT/sector re-fetch: if the WS payload's job has
            // null pct/sector but valid lat/lng (e.g. geocode succeeded
            // AFTER the broadcast was queued, or the job was created before
            // the auto-populate logic), GET the fresh job — the backend's
            // GET /jobs/{id} endpoint runs a defensive backfill that
            // resolves and persists pct/sector. The MDT then re-renders
            // with the correct precinct/sector instead of "NULLPCT".
            const j = msg.job;
            if (j?.id && j.lat != null && j.lng != null && (!j.pct || !j.sector)) {
              setTimeout(() => {
                axios.get(`${API}/jobs/${j.id}`).then((r) => {
                  if (r.data?.id !== j.id) return;
                  setAssignedCall((prev) => {
                    if (!prev || prev.job?.id !== j.id) return prev;
                    return { ...prev, job: r.data };
                  });
                }).catch(() => {});
              }, 300);
            }
            // Per operator spec: the F1/63 F-key timestamp must reflect WHEN
            // the crew actually presses 63 — NOT when the dispatcher assigned
            // the call. Assignment does NOT stamp this key; applyStatus(63)
            // does that at click time.
          } else if (msg.type === "message_received" && msg.message) {
            // Only react if THIS unit is the recipient OR sender (the broadcast
            // is fan-out so non-recipients ignore).
            const m = msg.message;
            const ldNow = logonDataRef.current;
            if (!ldNow) return;
            const meIsRecipient = m.recipient_type === "unit" && m.recipient_id === ldNow.unit_id;
            const meIsSender = m.sender_type === "unit" && m.sender_id === ldNow.unit_id;
            if (!meIsRecipient && !meIsSender) return;
            setInboxMessages((prev) => {
              if (prev.find((x) => x.id === m.id)) return prev;
              return [...prev, m];
            });
            // Ding + visual flash only on RECEIVED-and-not-yet-seen messages.
            if (meIsRecipient && !seenMessageIdsRef.current.has(m.id) && !m.read_at) {
              seenMessageIdsRef.current.add(m.id);
              playMessageDing();
            }
          } else if (msg.type === "message_read" && msg.message) {
            // A read receipt — just sync our local copy.
            setInboxMessages((prev) => prev.map((x) => (x.id === msg.message.id ? msg.message : x)));
          } else if (msg.type === "messages_bulk_read" && msg.recipient_type === "unit" && logonDataRef.current && msg.recipient_id === logonDataRef.current.unit_id) {
            // Bulk read-event: flip every unread inbox msg to read locally so
            // the MESSAGE WAITING badge clears without waiting for refetch.
            const nowIso = new Date().toISOString();
            setInboxMessages((prev) => prev.map((x) => (
              x.recipient_type === "unit" && x.recipient_id === msg.recipient_id && !x.read_at
                ? { ...x, read_at: nowIso } : x
            )));
          } else if (msg.type === "unit_history_added" && msg.unit_id === ld.unit_id) {
            // New entry was appended server-side (e.g. notification from dispatcher).
            // If the Unit History panel is open, refresh it so the crew sees it immediately.
            fetchUnitHistoryRef.current && fetchUnitHistoryRef.current();
          } else if (msg.type === "job_updated" && msg.job && (
            msg.job.assigned_unit_id === ld.unit_id ||
            (msg.job.assist_units || []).some((a) => (a?.unit_id || a) === ld.unit_id)
          )) {
            // Guard: only update assignedCall if the incoming job matches the
            // call the MDT is currently on. Otherwise a job_updated for a
            // stale/old call (e.g. the one the unit was on 5 minutes ago,
            // now completed) would overwrite the fresh call's call_info and
            // make the ASSIGNED chronology line disappear.
            setAssignedCall(prev => {
              if (prev?.job?.id && msg.job.id && prev.job.id !== msg.job.id) {
                return prev;  // ignore — this update is for a different call
              }
              return prev ? { ...prev, job: msg.job } : { job: msg.job };
            });
            if (msg.address_changed) {
              // Yellow UPDATE popup — mirrors CALL ASSIGNED popup style.
              setAddressUpdate({
                new_address: msg.job.location,
                old_address: msg.old_address || "",
                timestamp: new Date().toLocaleTimeString("en-GB"),
              });
            }
            if (msg.info_changed && !showCallInfo) {
              setHasUnseenInfo(true);
            }
          } else if (msg.type === "unit_updated" && msg.unit?.id === ld.unit_id) {
            const u = msg.unit;
            if (u.signal !== undefined) {
              const ts = new Date(u.status_changed_at || Date.now()).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
              setCurrentSignal(u.signal);
              setSignalTimestamp(ts);
              setStatusChangedAt(new Date(u.status_changed_at || Date.now()).getTime());
              setSignalTimestamps(prev => {
                if ([97, 98, 99].includes(u.signal)) return { [u.signal]: ts };
                const updated = { ...prev, [u.signal]: ts };
                // For signal 82 (transporting), also stamp the specific HA or HB key that was pressed
                if (u.signal === 82) {
                  const choice = hospitalChoiceRef.current;
                  if (choice === "HA" || choice === "HB") updated[choice] = ts;
                }
                return updated;
              });
              setOnCall(CALL_SIGNALS.includes(u.signal));
              // Capture hospital transport info
              if (u.hospital_transport?.hospital_code) {
                setLogonData(prev => prev ? { ...prev, hospital_code: u.hospital_transport.hospital_code } : prev);
              }
            }
          }
        } catch {}
      };
      ws.onclose = () => {
        clearAllTimers();
        reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    // Also re-establish when the tab regains focus (laptop wake, OS network change).
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        clearAllTimers();
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearAllTimers();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (ws) { try { ws.close(); } catch {} }
    };
  }, [loggedIn, logonData]);

  // Duration display for yellow box - DOS format: "13min" or "1h13min"
  const getDuration = () => {
    if (!statusChangedAt) return "0min";
    const elapsed = Math.floor((Date.now() - statusChangedAt) / 1000);
    const m = Math.floor(elapsed / 60);
    const h = Math.floor(m / 60);
    const rm = m % 60;
    if (h > 0) return `${h}h${String(rm).padStart(2, "0")}min`;
    return `${m}min`;
  };

  const isSupervisor = unitData?.unit_type === "SUPERVISOR";
  const crewNeeded = isSupervisor ? 1 : 2;
  // Supervisors cover all boroughs and are not required to pick one at logon.
  const canSubmit = agency && unitId && (isSupervisor || boroughCoverage) && crew1 && radio1 && (isSupervisor || (crew2 && radio2));

  const [logonError, setLogonError] = useState("");

  const handleLogon = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setLogonError("");
    try {
      const res = await axios.post(`${API}/crew/logon`, {
        agency, unit_id: unitId, borough_coverage: boroughCoverage, tour,
        crew_1: crew1, crew_2: crew2, crew_3: crew3, crew_4: crew4,
        radio_1: radio1, radio_2: radio2, radio_3: radio3, radio_4: radio4,
      });
      if (res.data?.error) {
        // Backend validation failure (e.g., VAC RCC mismatch). Show it; do NOT log on.
        setLogonError(res.data.error);
      } else {
        setLogonData(res.data);
        setLoggedIn(true);
        setShowLoggedInPopup(true);
        setForceLogonForm(false);  // clear re-logon override on success
        // If the unit already has an assigned active job (e.g., dispatcher assigned before
        // the crew logged on, or crew refreshed the page mid-call), hydrate assignedCall.
        try {
          const jobsRes = await axios.get(`${API}/jobs`);
          const active = (jobsRes.data || []).find(j =>
            (j.assigned_unit_id === res.data.unit_id ||
             (j.assist_units || []).some((a) => (a?.unit_id || a) === res.data.unit_id)) &&
            !["completed", "cancelled"].includes(j.status)
          );
          if (active) {
            // Mirror the job_assigned WS payload shape so the CALL INFO panel renders correctly.
            const ctRes = await axios.get(`${API}/calltypes`).catch(() => ({ data: [] }));
            const ct = (ctRes.data || []).find(c => c.call_type === active.call_type);
            setAssignedCall({
              type: "job_assigned",
              unit_id: res.data.unit_id,
              job: active,
              call_code: ct?.call_code || "",
              call_type_full: active.call_type || "",
              segment: ct?.segment || "",
              crew: res.data.crew || {},
            });
            setOnCall(true);
          }
        } catch {}
      }
    } catch (err) {
      console.error(err);
      setLogonError(err?.response?.data?.error || "Logon failed. Please try again.");
    }
    setSubmitting(false);
  }, [canSubmit, agency, unitId, boroughCoverage, tour, crew1, crew2, crew3, crew4, radio1, radio2, radio3, radio4]);

  const applyStatus = async (signal) => {
    if (!logonData) return;
    const statusMap = { 63: "en_route", 84: "on_scene", 82: "transporting", 81: "at_hospital", 97: "available", 98: "returning", 99: "available_hq", 89: "at_post" };
    try {
      const res = await axios.put(`${API}/units/${logonData.unit_id}/status`, { status: statusMap[signal] });
      // Backend returns {error: "..."} (HTTP 200) on signal-progression
      // violations. Do NOT optimistically flip the MDT's local signal or
      // timestamps in that case — it causes the dispatcher board to stay
      // stale while the MDT incorrectly shows the new signal. Surface the
      // error to the crew so they see why the change didn't take.
      if (res && res.data && res.data.error) {
        setLogonError(res.data.message || res.data.error);
        return;
      }
      setLogonError("");
      const ts = getMilTime();
      setCurrentSignal(signal);
      setSignalTimestamp(ts);
      setStatusChangedAt(Date.now());
      // Leaving signal 81 clears any extended disposition annotation
      if (signal !== 81) setExtendedStatus(null);
      // Clear HA/HB key highlight once we leave 82/81 (call is ending via availability)
      if ([97, 98, 99, 89].includes(signal)) setHospitalChoice(null);
      // Available signals end the current call — wipe assignedCall so CALL INFO reverts to grey.
      if ([97, 98, 99, 89].includes(signal)) {
        setAssignedCall(null);
        setShowCallInfo(false);
        setShowMciList(false);
        setHasUnseenInfo(false);
        setAddressUpdate(null);
      }
      // Record timestamp for this signal — all F-keys (including 63) stamp
      // at the moment of the click.
      setSignalTimestamps(prev => {
        const updated = { ...prev, [signal]: ts };
        // If going to available (97/98/99), clear all call timestamps
        if ([97, 98, 99].includes(signal)) {
          return { [signal]: ts };
        }
        return updated;
      });
      setOnCall(CALL_SIGNALS.includes(signal));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || "";
      if (msg) setLogonError(msg);
    }
  };

  const confirmStatus = async () => {
    if (!showStatusPopup) return;
    // If from 81, going to 97/98/99 clears the call
    if (currentSignal === 81 && [97, 98, 99].includes(showStatusPopup)) {
      // All 97/98/99 from 81 behave the same - clear the call
      await applyStatus(showStatusPopup);
      setOnCall(false);
      setAssignedCall(null);
      setShowCallInfo(false);
      setSignalTimestamps({});
    } else {
      await applyStatus(showStatusPopup);
    }
    setShowStatusPopup(null);
  };

  // DISPO button definitions matching the reference image
  // 81A/81S/81T: only enabled when signal is 81
  // 83D/83R/91/93/94/95/96: only enabled when signal is 84
  const dispoButtons = [
    { code: "81A", label: "Onscene at Hospital\n- Admin", auto98: false, requireSignal: 81 },
    { code: "81S", label: "Onscene at Hospital\n- Stretcher", auto98: false, requireSignal: 81 },
    { code: "81T", label: "Onscene at Hospital\n- Triage", auto98: false, requireSignal: 81 },
    { code: "83D", label: "DOA Onscene", auto98: true, requireSignal: 84 },
    { code: "83R", label: "DOA\nResuscitation", auto98: true, requireSignal: 84 },
    { code: "91", label: "Incident\nNo Patient", auto98: true, requireSignal: 84 },
    { code: "93", label: "Refused\nMedical Aid", auto98: true, requireSignal: 84 },
    { code: "94", label: "Treated at Scene\nbut not Transported", auto98: true, requireSignal: 84 },
    { code: "95", label: "Triaged out by\nMedical Control", auto98: true, requireSignal: 84 },
    { code: "96", label: "Gone on\nArrival", auto98: true, requireSignal: 84 },
  ];

  const handleDispo = async (btn) => {
    if (!logonData) return;
    try {
      await axios.post(`${API}/units/${logonData.unit_id}/disposition`, {
        unit_id: logonData.unit_id,
        code: btn.code,
      });
      // 81A / 81S / 81T: stay at signal 81, just annotate extended status
      if (!btn.auto98) {
        const extMap = { "81A": "EXTENDED PAPERWORK", "81S": "EXTENDED STRETCHER", "81T": "EXTENDED TRIAGE" };
        setExtendedStatus(extMap[btn.code] || null);
        setShowDispoPanel(false);
        return;
      }
      // All other dispositions move unit to 98
      setCurrentSignal(98);
      const ts = getMilTime();
      setSignalTimestamp(ts);
      setStatusChangedAt(Date.now());
      setOnCall(false);
      setAssignedCall(null);
      setShowCallInfo(false);
      setSignalTimestamps({ 98: ts });
      setExtendedStatus(null);
      setHospitalChoice(null);
      setShowDispoPanel(false);
    } catch {}
  };

  // Derived data
  //
  // Sister-agency sharing: Bedford Stuyvesant VAC and Central Brooklyn VAC
  // share personnel, radios, and unit logon options. When a unit is added
  // under either agency, crew members from BOTH agencies appear in the
  // CADLOGON crew dropdowns. Radios from both agency records are also
  // merged so the crew can choose any shared radio.
  const SISTER_AGENCIES_MAP = {
    "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS": "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS",
    "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS": "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS",
  };
  const sisterAgency = SISTER_AGENCIES_MAP[agency] || "";
  const effectiveAgencies = [agency, sisterAgency].filter(Boolean);
  const agencyUnits = allUnits.filter((u) => u.agency === agency);
  const agencyUsers = users.filter((u) => {
    if (effectiveAgencies.includes(u.agency)) return true;
    return Array.isArray(u.agencies) && u.agencies.some((a) => effectiveAgencies.includes(a));
  });
  const sisterAgencyData = sisterAgency ? agencies.find((a) => a.name === sisterAgency) : null;
  const allAgencyRadios = Array.from(new Set([
    ...(agencyData?.radios || []),
    ...(sisterAgencyData?.radios || []),
  ]));

  // Authorized Crew Roster — if the selected unit has an `authorized_crew_ids`
  // list, the Crew 1–4 dropdowns restrict to ONLY those members (crew swap,
  // per-unit restrictions). Empty list → no restriction (show all sister-
  // agency users).
  // ALSO restrict crew list to users whose `assignments` include "AMBULANCE"
  // — dispatchers (DISPATCHER-only assignment) must not appear as selectable
  // ambulance crew members.
  const selectedUnitForLogon = allUnits.find((u) => u.id === unitId);
  const authorizedIdsOnUnit = selectedUnitForLogon?.authorized_crew_ids || [];
  // Iter 115 — Ambulance MDT crew dropdown must only surface users
  // whose roster row explicitly lists `AMBULANCE` in `assignments`.
  // Dispatchers, supervisors, and users with an empty assignment
  // column (older imports) no longer sneak in. Agency admins have to
  // stamp the AMBULANCE assignment on anyone who should be selectable
  // as crew.
  const ambulanceAssignedUsers = agencyUsers.filter((u) => {
    const assigns = (u.assignments || []).map((a) => (a || "").toUpperCase());
    return assigns.includes("AMBULANCE");
  });
  const rosterScopedAgencyUsers = authorizedIdsOnUnit.length > 0
    ? ambulanceAssignedUsers.filter((u) => authorizedIdsOnUnit.includes(u.id))
    : ambulanceAssignedUsers;

  // Duplicate prevention: hide crew members and radios currently in use by
  // any OTHER logged-on unit (across both sister agencies). We exclude the
  // unit that THIS MDT session is (re)logging into so the crew can still
  // see their own current selections during re-logon.
  const { busyCrew, busyRadios } = (() => {
    const crewSet = new Set();
    const radioSet = new Set();
    for (const u of allUnits) {
      if (!u.logged_in) continue;
      if (unitId && u.id === unitId) continue; // skip the unit this MDT is signing in
      const c = u.crew || {};
      ["crew_1", "crew_2", "crew_3", "crew_4"].forEach((k) => {
        const name = (c[k] || "").trim();
        if (name) crewSet.add(name);
      });
      ["radio_1", "radio_2", "radio_3", "radio_4"].forEach((k) => {
        const r = (c[k] || "").trim();
        if (r) radioSet.add(r);
      });
    }
    return { busyCrew: crewSet, busyRadios: radioSet };
  })();

  const crewOptions = rosterScopedAgencyUsers
    .map((u) => `${u.first_name} ${u.last_name}`)
    .filter((n) => !busyCrew.has(n));
  const agencyRadios = allAgencyRadios.filter((r) => !busyRadios.has(r));
  const selectedUnit = allUnits.find((u) => u.id === unitId);
  // 10-13 emergency flag — read from the currently-logged-on unit record.
  // selectedUnit is the logon-form selection; once logged in, match via logonData.
  const myUnit = loggedIn && logonData?.unit_id ? allUnits.find((u) => u.id === logonData.unit_id) : selectedUnit;
  const emergencyActive = !!myUnit?.emergency_active;
  const tours = ["Tour 1 (0700-1500)", "Tour 2 (1500-2300)", "Tour 3 (2300-0700)"];

  // Borough coverage - if multiple, crew must select one
  const boroughOptions = (agencyData?.borough_coverage || "").split(",").map(b => b.trim()).filter(Boolean);
  const hasMutipleBoroughs = boroughOptions.length > 1;

  const leftFields = [
    // Iter 83 — when MAC-bound, agency is locked too (it follows the unit).
    emsBindState.bound && emsBindState.boundUnit
      ? { label: "Agency", value: emsBindState.boundUnit.agency || "", readOnly: true }
      : { label: "Agency", value: agency, setter: setAgency, options: agencies.map(a => a.name) },
    // Supervisors cover all boroughs — hide the dropdown entirely for them.
    { label: "Borough Coverage", value: boroughCoverage, setter: hasMutipleBoroughs ? setBoroughCoverage : null, options: hasMutipleBoroughs ? boroughOptions : [], readOnly: !hasMutipleBoroughs, hide: isSupervisor },
    // Crew 1-4 share the same unrestricted option list — any crew member
    // eligible for Crew 1 is equally eligible for Crew 2/3/4 (per operator
    // spec). Duplicate-prevention happens at the agency level (via `busyCrew`
    // excluding members already logged on another unit), not between slots on
    // the same logon form.
    { label: "Crew 1", value: crew1, setter: setCrew1, options: crewOptions },
    { label: "Crew 2", value: crew2, setter: setCrew2, options: crewOptions, hide: isSupervisor },
    { label: "Crew 3", value: crew3, setter: setCrew3, options: crewOptions },
    { label: "Crew 4", value: crew4, setter: setCrew4, options: crewOptions },
  ];

  const rightFields = [
    // Iter 83 — when this terminal is auto-resolved via MAC binding, the
    // Unit dropdown is hidden and replaced by a read-only label of the
    // bound unit. Operator's spec: "take off the ambulance selection from
    // the log on screen for the units since we are doing mac address
    // binding". The dispatcher can still manually override via the
    // "Manual override" link surfaced in the unbind banner.
    emsBindState.bound && emsBindState.boundUnit
      ? {
          label: "Unit",
          value: `${emsBindState.boundUnit.name} (${emsBindState.boundUnit.unit_type || "N/A"})`,
          readOnly: true,
        }
      : { label: "Unit", value: unitId, setter: setUnitId, options: agencyUnits.map(u => ({ value: u.id, label: `${u.name} (${u.unit_type || "N/A"})` })), isUnit: true },
    { label: "Tour", value: tour, setter: setTour, options: tours },
    { label: "Radio 1", value: radio1, setter: setRadio1, options: agencyRadios },
    { label: "Radio 2", value: radio2, setter: setRadio2, options: agencyRadios.filter(r => r !== radio1), hide: isSupervisor },
    { label: "Radio 3", value: radio3, setter: setRadio3, options: agencyRadios.filter(r => r !== radio1 && r !== radio2) },
    { label: "Radio 4", value: radio4, setter: setRadio4, options: agencyRadios.filter(r => r !== radio1 && r !== radio2 && r !== radio3) },
  ];

  const fetchUnitHistory = async () => {
    if (!logonData) return;
    try {
      const res = await axios.get(`${API}/units/${logonData.unit_id}/history`);
      setUnitHistory(res.data);
    } catch {}
  };
  // Stable ref so the WebSocket handler (defined earlier) can call the latest fetcher.
  const fetchUnitHistoryRef = useRef(fetchUnitHistory);
  useEffect(() => { fetchUnitHistoryRef.current = fetchUnitHistory; });

  // Initial messages fetch on login + whenever logonData changes.
  useEffect(() => {
    if (loggedIn && logonData?.unit_id) fetchMessages();
  }, [loggedIn, logonData, fetchMessages]);

  // Pull the online roster whenever the MDT messages canvas opens (and every
  // 30s while it's open) so the SUPV NEW MSG dropdown has fresh options.
  useEffect(() => {
    if (!showMessages) return;
    const myId = logonData?.unit_id;
    const fetchRoster = () => {
      axios.get(`${API}/messages/online-roster`).then((r) => {
        const dispatchers = (r.data?.dispatchers || []).map((d) => ({
          type: "dispatcher",
          id: d.user_id,
          name: d.dispatcher_userid || d.username,
        }));
        const units = (r.data?.units || [])
          .filter((u) => u.id !== myId)
          .map((u) => ({ type: "unit", id: u.id, name: u.name }));
        const list = [...dispatchers, ...units];
        composeRosterRef.current = list;
        setComposeRoster(list);
      }).catch(() => {});
    };
    fetchRoster();
    const t = setInterval(fetchRoster, 30000);
    return () => clearInterval(t);
  }, [showMessages, logonData]);

  const handleLogoff = () => {
    // Only allow logoff from available signals (97, 98, 99)
    if (loggedIn && ![97, 98, 99].includes(currentSignal)) return;
    setShowLogoffConfirm(true);
  };

  const confirmLogoff = async () => {
    if (logonData) {
      try {
        // Log the logoff event to unit history
        await axios.post(`${API}/units/${logonData.unit_id}/disposition`, {
          unit_id: logonData.unit_id,
          code: "LOGOFF",
          comment: "Unit logged off",
        });
        // Set unit as logged out on backend
        await axios.put(`${API}/units/${logonData.unit_id}/logoff`);
      } catch {}
    }
    // Clear session
    localStorage.removeItem("crew_session");
    setLoggedIn(false);
    setLogonData(null);
    setCurrentSignal(null);
    setSignalTimestamp(null);
    setSignalTimestamps({});
    setStatusChangedAt(null);
    setOnCall(false);
    setAssignedCall(null);
    setShowCallInfo(false);
    setShowDispoPanel(false);
    setShowUnitHistory(false);
    setShowLogoffConfirm(false);
    setShowHospitalPopup(false);
    setShowScratchPad(false);
    setScratchText("");
    setShowMciList(false);
    setShowCallAssignedPopup(false);
    setAddressUpdate(null);
    setHasUnseenInfo(false);
    // Per operator spec: a clean slate when the unit logs off — wipe both
    // the unit history (UH screen) and the message history (MSG LIST +
    // any local system messages from the command bar) so the next crew
    // logging onto this MDT starts fresh.
    setUnitHistory([]);
    setInboxMessages([]);
    setSystemMessages([]);
    setSelectedMessageId(null);
    setComposeMode(null);
    setShowMessages(false);
    setBottomMessage("");
    setCommandInput("");
    // Reset form fields for fresh logon
    setAgency("");
    setUnitId("");
    setBoroughCoverage("");
    setTour("");
    setCrew1("");
    setCrew2("");
    setCrew3("");
    setCrew4("");
    setRadio1("");
    setRadio2("");
    setRadio3("");
    setRadio4("");
  };

  // Format an ISO timestamp as HH:MM:SS in the agency local timezone.
  // Used for the MESSAGE WAITING popup + MSG LIST chronology.
  const militaryHMSFromIso = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch { return ""; }
  };

  // Scratch pad free-type state (local to this MDT, not persisted).
  // Opened via the SCRATCH PAD button in the top bar; clears on logoff.
  const [showScratchPad, setShowScratchPad] = useState(false);
  const [scratchText, setScratchText] = useState("");

  // ─── Derived message counters ───
  // Inbox holds both sent + received. Unread = received & not yet read,
  // and the new MESSAGE WAITING flow shows them oldest-first via the dedicated
  // popup. The MSG LIST tab shows everything chronologically (sent + received).
  const myUnitId = logonData?.unit_id;
  const unreadIncoming = [...inboxMessages, ...systemMessages]
    .filter((m) => m.recipient_type === "unit" && m.recipient_id === myUnitId && !m.read_at)
    .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
  const unreadCount = unreadIncoming.length;
  // Reply / compose authorization. Per operator: AMBULANCE and EASV units
  // are READ-ONLY. Supervisor units, command vehicles, and any other unit
  // type may reply / compose. Blank unit_type is treated as AMBULANCE for
  // safety (matches backend behaviour). Use `myUnit` (the actually-logged-on
  // unit) as the source of truth — `selectedUnit` is the LOGIN-form dropdown
  // selection and gets cleared once logon finishes, which silently dropped
  // SUPV authorization on the live MDT.
  const canSendMessages = (() => {
    const t = ((myUnit?.unit_type || logonData?.unit_type || "") + "").toUpperCase();
    if (t === "" || t === "AMBULANCE" || t === "EASV") return false;
    return true;
  })();
  // Legacy alias kept so the older inline references compile until they're
  // all renamed below.
  const isSupvForMessaging = canSendMessages;

  const openOldestUnread = () => {
    // Per operator spec: MESSAGE WAITING shows ONE unread message at a time.
    // Each press advances to the NEXT unread. The MESSAGE WAITING counter
    // must drop the moment a message is displayed — not on the next press.
    // The right-side history panel is NEVER opened from this button —
    // that's MSG LIST's exclusive job.
    if (!loggedIn) return;

    // Mark the currently-displayed unread (if any) as read so the
    // count decrements before we pick the next. This is what makes each
    // subsequent press advance to the next message.
    if (showMessages === "waiting" && selectedMessageId) {
      const cur = unreadIncoming.find((m) => m.id === selectedMessageId);
      if (cur) {
        if (cur._system_local || cur.sender_type === "system") {
          setSystemMessages((prev) => prev.map((m) => (m.id === cur.id ? { ...m, read_at: new Date().toISOString() } : m)));
        } else {
          axios.put(`${API}/messages/${cur.id}/read`).catch(() => {});
          setInboxMessages((prev) => prev.map((m) => (m.id === cur.id ? { ...m, read_at: new Date().toISOString() } : m)));
        }
      }
    }

    // Pick the NEXT oldest unread that is NOT the one we just acked.
    const nextUnread = unreadIncoming.find((m) => m.id !== selectedMessageId);
    closeAllPanels();
    setShowMessages("waiting");
    if (nextUnread) {
      setSelectedMessageId(nextUnread.id);
      // Iter 90 — counter must drop the instant the message is displayed.
      // Previously the message was only marked read on the NEXT button
      // press, forcing the operator to press MESSAGE WAITING twice before
      // the badge decremented. Mark it read NOW so the unread count
      // reflects what's actually been viewed.
      const readAt = new Date().toISOString();
      if (nextUnread._system_local || nextUnread.sender_type === "system") {
        setSystemMessages((prev) => prev.map((m) => (m.id === nextUnread.id ? { ...m, read_at: readAt } : m)));
      } else {
        axios.put(`${API}/messages/${nextUnread.id}/read`).catch(() => {});
        setInboxMessages((prev) => prev.map((m) => (m.id === nextUnread.id ? { ...m, read_at: readAt } : m)));
      }
    } else {
      // No more unread → empty state in waiting pane.
      setSelectedMessageId(null);
    }
  };

  const goPrevMessage = () => {
    // Step BACKWARD chronologically through received messages — not unread.
    // Per operator spec the PREV MSG button lets the crew page through their
    // received history one at a time.
    const incoming = inboxMessages
      .filter((m) => m.recipient_type === "unit" && m.recipient_id === myUnitId)
      .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
    if (incoming.length === 0) return;
    if (!selectedMessageId) {
      setSelectedMessageId(incoming[incoming.length - 1].id);
      return;
    }
    const idx = incoming.findIndex((m) => m.id === selectedMessageId);
    if (idx > 0) setSelectedMessageId(incoming[idx - 1].id);
  };

  // ─── MDT Command Prompt: submit the typed command on Enter ───
  // Backend parses the verb, runs the matching handler, and returns either
  // an `output` string (rendered in MSG LIST as a SYSTEM-sourced message)
  // or `denied: true` (regular-AMB/EASV tried a SUPV-only command — flash
  // "I can't let you do that!" in the bottom bar instead).
  const submitCommand = async () => {
    const cmd = (commandInput || "").trim();
    if (!cmd) return;
    // Gate 1: must be logged on. Surface a clear message instead of
    // silently swallowing the keystroke (the original cause of "I'm
    // getting errors" reports — the bar was a no-op pre-logon).
    if (!loggedIn || !logonData?.unit_id) {
      setBottomMessage("MUST BE LOGGED ON TO RUN COMMANDS");
      setTimeout(() => setBottomMessage(""), 4000);
      setCommandInput("");
      return;
    }
    try {
      const res = await axios.post(`${API}/mdt/command`, { unit_id: logonData.unit_id, command: cmd });
      const data = res.data || {};
      if (data.denied) {
        setBottomMessage(data.denial_message || "I can't let you do that!");
        // Auto-clear after 4 seconds back to the standard footer text.
        setTimeout(() => setBottomMessage(""), 4000);
      } else if (data.output) {
        // Render the output as a synthetic system message in MSG LIST.
        // Per operator: the output queues like a normal message — it does
        // NOT auto-open the canvas. The crew clicks MESSAGE WAITING (or
        // MSG LIST) to read it, just like a real incoming message.
        const sysMsg = {
          id: `sys-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          thread_id: null,
          sender_type: "system",
          sender_id: "CAD",
          sender_name: "CAD",
          recipient_type: "unit",
          recipient_id: logonData.unit_id,
          recipient_name: logonData.unit_name || "",
          body: data.output,
          timestamp: new Date().toISOString(),
          read_at: null,            // unread → bumps MESSAGE WAITING badge
          deleted_by_recipient: false,
          deleted_by_sender: false,
          // Mark so renderers know not to send PUT /read for these.
          _system_local: true,
        };
        setSystemMessages((prev) => [...prev, sysMsg]);
        // Per operator: queue as a real-style message — flash the footer
        // so the crew knows the command output is waiting in MSG LIST.
        setBottomMessage("MESSAGE WAITING");
        setTimeout(() => setBottomMessage(""), 4000);
      } else if (data.error) {
        setBottomMessage(`CMD ERROR: ${data.error}`);
        setTimeout(() => setBottomMessage(""), 4000);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "network failure";
      setBottomMessage(`CMD ERROR: ${detail}`);
      setTimeout(() => setBottomMessage(""), 5000);
    }
    setCommandInput("");
  };


  const handleMsgListDelete = async (m) => {
    // System (locally-generated) messages aren't persisted — just remove locally.
    if (m._system_local || m.sender_type === "system") {
      setSystemMessages((prev) => prev.filter((x) => x.id !== m.id));
      if (selectedMessageId === m.id) setSelectedMessageId(null);
      return;
    }
    try {
      await axios.delete(`${API}/messages/${m.id}`, { params: { requester_type: "unit", requester_id: myUnitId } });
      setInboxMessages((prev) => prev.filter((x) => x.id !== m.id));
      if (selectedMessageId === m.id) setSelectedMessageId(null);
    } catch {}
  };

  const startReply = () => {
    if (!isSupvForMessaging) return;
    const sel = inboxMessages.find((m) => m.id === selectedMessageId);
    if (!sel) return;
    setComposeMode("reply");
    setComposeTo(sel.sender_name || "");
    setComposeBody("");
  };

  const startNewMessage = () => {
    if (!isSupvForMessaging) return;
    setComposeMode("new");
    setComposeTo("");
    setComposeBody("");
  };

  const cancelCompose = () => {
    setComposeMode(null);
    setComposeBody("");
    setComposeTo("");
  };

  const submitCompose = async () => {
    if (!isSupvForMessaging || !composeBody.trim()) return;
    let recipients = [];
    const sel = inboxMessages.find((m) => m.id === selectedMessageId);
    if (composeMode === "reply" && sel) {
      recipients = [{ type: sel.sender_type, id: sel.sender_id, name: sel.sender_name }];
    } else if (composeMode === "new") {
      // composeTo is either "ALL" (send-all) OR a JSON-encoded recipient picked from the dropdown.
      if (composeTo === "ALL") {
        recipients = (composeRosterRef.current || []).map((r) => ({ type: r.type, id: r.id, name: r.name }));
      } else {
        try {
          const parsed = JSON.parse(composeTo);
          recipients = [parsed];
        } catch { return; }
      }
    }
    if (recipients.length === 0) return;
    try {
      await axios.post(`${API}/messages`, {
        sender_type: "unit",
        sender_id: myUnitId,
        sender_name: logonData?.unit_name || "",
        recipients,
        body: composeBody.trim(),
        in_reply_to: composeMode === "reply" ? selectedMessageId : null,
      });
      setComposeMode(null);
      setComposeBody("");
      setComposeTo("");
      fetchMessages();
    } catch {}
  };

  const closeAllPanels = () => {
    // Per operator spec: CLEAR SCREEN must wipe EVERY piece of on-screen
    // content regardless of which view is active. This includes all panels,
    // all overlays, any selected message, any compose draft, any transient
    // notices, the bottom-of-screen message bar, and the command prompt
    // input. After CLEAR SCREEN the MDT returns to its default black
    // resting view.
    setShowCallInfo(false);
    setShowDispoPanel(false);
    setShowUnitHistory(false);
    setShowMciList(false);
    setShowHospitalPopup(false);
    setShowCallAssignedPopup(false);
    setShowScratchPad(false);
    setShowMessages(false);
    setComposeMode(null);
    setComposeBody("");
    setComposeTo("");
    setSelectedMessageId(null);
    setAddressUpdate(null);
    setHasUnseenInfo(false);
    setBottomMessage("");
    setCommandInput("");
    setShowLogoffConfirm(false);
  };

  // ─── Active-canvas detector ───
  // Used by the top-bar function-key buttons so pressing CALL INFO / MCI
  // LIST / MSG LIST / UNIT HISTORY immediately switches the canvas to that
  // view, regardless of what's currently showing. Without this, pressing
  // a button when its panel state was stale-true (because another popup
  // was masking it) would TOGGLE OFF instead of bringing the panel up.
  const activeCanvas =
    showHospitalPopup ? "hospital" :
    showMessages ? "msg" :
    showMciList ? "mci" :
    showCallInfo ? "callinfo" :
    showUnitHistory ? "history" :
    showDispoPanel ? "dispo" :
    showScratchPad ? "scratch" :
    "default";

  const switchTo = (target) => {
    // Per operator spec: a single button press immediately opens its
    // panel. Buttons do NOT toggle off when re-pressed — pressing the
    // same button again is a no-op (the panel stays open). To leave
    // a panel, the crew presses a different function-key button.
    setShowCallInfo(false);
    setShowMciList(false);
    setShowMessages(false);
    setShowUnitHistory(false);
    setShowDispoPanel(false);
    setShowHospitalPopup(false);
    setShowScratchPad(false);
    setComposeMode(null);
    if (target === "callinfo") setShowCallInfo(true);
    else if (target === "mci") setShowMciList(true);
    else if (target === "msg") setShowMessages("list");
    else if (target === "history") setShowUnitHistory(true);
  };

  const topTabs = [
    { label: ["CALL", "INFO"], active: loggedIn && showCallInfo, action: () => { if (loggedIn && assignedCall) { switchTo("callinfo"); setHasUnseenInfo(false); } } },
    { label: ["LOCATION", "DATA"] },
    { label: ["MCI LIST"], active: loggedIn && showMciList, action: () => { if (loggedIn && assignedCall?.job?.mci) { switchTo("mci"); } } },
    { label: ["MSG LIST"], active: showMessages, action: () => {
        if (!loggedIn) return;
        switchTo("msg");
        // Opening MSG LIST reads everything in the inbox — bulk-clear any
        // residual unread state so the MESSAGE WAITING button auto-greys
        // even if an individual per-message read PUT had previously failed.
        if (myUnitId) {
          axios.put(`${API}/messages/mark-all-read`, null, {
            params: { user_type: "unit", user_id: myUnitId },
          }).catch(() => {});
          const nowIso = new Date().toISOString();
          setInboxMessages((prev) => prev.map((m) =>
            m.recipient_type === "unit" && m.recipient_id === myUnitId && !m.read_at
              ? { ...m, read_at: nowIso } : m
          ));
          setSystemMessages((prev) => prev.map((m) =>
            m.recipient_type === "unit" && m.recipient_id === myUnitId && !m.read_at
              ? { ...m, read_at: nowIso } : m
          ));
        }
      } },
    { label: ["UNIT HISTORY"], active: showUnitHistory, action: () => { if (loggedIn) { fetchUnitHistory(); switchTo("history"); } } },
    // CLEAR SCREEN — hide EVERY popup/panel so the MDT goes back to its
    // default resting view. Works even when multiple panels are stacked.
    { label: ["CLEAR SCREEN"], action: closeAllPanels },
    { label: ["LOGON"], selected: !loggedIn || forceLogonForm, action: () => {
        // LOGON is ALWAYS enabled — crew can tap it any time to open the
        // CADLOGON form, either for the initial logon or to re-log with a
        // crew/radio swap. If logged in, pre-fill the form from the current
        // unit's crew record so the crew only has to change what's different.
        if (loggedIn && logonData) {
          setAgency(logonData.agency || "");
          setUnitId(logonData.unit_id || "");
          setBoroughCoverage(logonData.borough_coverage || "");
          setTour(logonData.tour || "");
          const c = logonData.crew || logonData || {};
          setCrew1(c.crew_1 || ""); setCrew2(c.crew_2 || ""); setCrew3(c.crew_3 || ""); setCrew4(c.crew_4 || "");
          setRadio1(c.radio_1 || ""); setRadio2(c.radio_2 || ""); setRadio3(c.radio_3 || ""); setRadio4(c.radio_4 || "");
          setForceLogonForm(true);
          // Hide other views so the form owns the screen.
          setShowCallInfo(false);
          setShowUnitHistory(false);
          setShowDispoPanel(false);
          setShowHospitalPopup(false);
          setShowMciList(false);
          setShowScratchPad(false);
        } else if (!loggedIn && canSubmit) {
          handleLogon();
        }
    } },
    { label: ["LOGOFF"], action: handleLogoff },
  ];

  const functionKeys = [
    { code: "63", key: "F1", signal: 63, callOnly: true },
    { code: "84", key: "F2", signal: 84, callOnly: true },
    { code: "HA", key: "F3", callOnly: true },
    { code: "HB", key: "F4", callOnly: true },
    { code: "81", key: "F5", signal: 81, callOnly: true },
    { code: "97", key: "F6", signal: 97 },
    { code: "98", key: "F7", signal: 98 },
    { code: "99", key: "F8", signal: 99 },
    { code: "89", key: "F9", signal: 89, geoOnly: true },
  ];

  // Signal progression order for crew (forward only, no going backwards)
  // DISP(10) -> 63 -> 84 -> HA/HB(82) -> 81
  const SIGNAL_ORDER = { 10: -1, 63: 0, 84: 1, 82: 2, 81: 3 };

  const canCrewChangeSignal = (targetSignal) => {
    // When on a call (10, 63, 84, 82), crew CANNOT go to 97/98/99 directly
    // Must use disposition buttons which auto-move to 98
    // Exception: from 81 (at hospital), CAN go to 97/98/99 to clear the call
    // Exception: OOS call types / FLAGGED DOWN auto-on-scene jobs — crew is 84 and the
    // only path to clear is directly to 97/98/99 (no transport, no disposition).
    const callActiveSignals = [10, 63, 84, 82];
    const isAutoOnScene = !!assignedCall?.job?.auto_on_scene;
    if (isAutoOnScene && currentSignal === 84 && [97, 98, 99].includes(targetSignal)) {
      return true;
    }
    if (callActiveSignals.includes(currentSignal) && [97, 98, 99].includes(targetSignal)) {
      return false;
    }
    if (currentSignal === 81 && [97, 98, 99].includes(targetSignal)) {
      return true;
    }
    // Available signals can transition between each other
    if (!onCall && [97, 98, 99].includes(targetSignal)) return true;
    // Call-only signals require being on a call (DISP counts as on-call)
    if (!onCall && ![97, 98, 99].includes(targetSignal)) return false;
    // Can't go backwards in call progression
    const currentOrder = SIGNAL_ORDER[currentSignal];
    const targetOrder = SIGNAL_ORDER[targetSignal];
    if (currentOrder !== undefined && targetOrder !== undefined) {
      if (targetOrder <= currentOrder) return false;
    }
    return true;
  };

  const handleFnKey = async (fk) => {
    if (!loggedIn || !logonData) return;
    if (fk.geoOnly) return;
    if (fk.callOnly && !onCall) return;
    if (fk.signal && !canCrewChangeSignal(fk.signal)) return;
    if ((fk.code === "HA" || fk.code === "HB") && !canCrewChangeSignal(82)) return;

    if (showLoggedInPopup) setShowLoggedInPopup(false);

    if (fk.code === "HA" || fk.code === "HB") {
      // Remember which hospital-transport key was pressed so we can stamp its own timestamp.
      setHospitalChoice(fk.code);
      // Open hospital transport popup
      setHospitalCategory("");
      setHospitalOverride("");
      setHospitalCloser("");
      setHospitalPatientReq("");
      setHospitalSupv("");
      setHospitalOlmc("");
      setHospitalAltDest("");

      // Reference point for closest-hospital calculation:
      // 1) Prefer the geocoded emergency call location (job.lat/job.lng).
      // 2) Fallback to live unit GPS from Traccar positions.
      let refLat = null, refLng = null;
      const job = assignedCall?.job;
      if (job && typeof job.lat === "number" && typeof job.lng === "number") {
        refLat = job.lat;
        refLng = job.lng;
      } else {
        const gpsUnit = (window._gpsPositions || []).find(p => p.mapped_unit_id === logonData.unit_id);
        if (gpsUnit) { refLat = gpsUnit.latitude; refLng = gpsUnit.longitude; }
      }

      if (refLat && refLng && hospitals.length > 0) {
        // Haversine distance (km) — much more accurate than Euclidean on lat/lng.
        const haversineKm = (lat1, lon1, lat2, lon2) => {
          const R = 6371;
          const toRad = (d) => d * Math.PI / 180;
          const dLat = toRad(lat2 - lat1);
          const dLon = toRad(lon2 - lon1);
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
          return 2 * R * Math.asin(Math.sqrt(a));
        };

        // Closest hospital overall
        let minDist = Infinity, nearest = "";
        for (const h of hospitals) {
          if (!h.lat || !h.lng) continue;
          const d = haversineKm(refLat, refLng, h.lat, h.lng);
          if (d < minDist) { minDist = d; nearest = h.hospital_code || h.name; }
        }
        setClosestHospital(nearest);

        // Closest hospital per category (case-insensitive, tolerate upload typos).
        const defaultCats = ["Adult GED", "Critical Adult", "CRITICAL PEDS", "OBS", "STROKE", "BURN", "STEMI", "LVAD", "SAFE", "PED EDP", "ADULT EDP", "Peds Trauma", "Adult Trauma", "HYPERBARIC", "PEDS GED", "VENOM", "LVO"];
        const normalize = (s) => (s || "")
          .toString()
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, " ")
          .replace(/\bCRTITICAL\b/g, "CRITICAL")
          .trim();
        const catHospitals = {};
        // Strict per-category diversion lookup: returns true only when an
        // active diversion's normalized category equals the chosen one
        // exactly. Substring matching would incorrectly cross categories
        // (e.g. "Adult ED" diversion would knock out "Adult EDP" picks).
        const isDiverted = (hospitalId, catName) => {
          if (!hospitalId || !catName || !activeDiversions.length) return false;
          const target = normalize(catName);
          return activeDiversions.some((d) =>
            d.hospital_id === hospitalId && normalize(d.category || "") === target
          );
        };
        defaultCats.forEach(cat => {
          const catN = normalize(cat);
          let bestDist = Infinity, bestH = null;
          for (const h of hospitals) {
            if (!h.lat || !h.lng) continue;
            const caps = (h.capabilities || []).map(normalize);
            // Match if any capability equals or contains the normalized category
            const match = caps.some(c => c === catN || c.includes(catN) || catN.includes(c));
            if (!match) continue;
            // Skip hospitals on active diversion for THIS category — the
            // CCC screen should auto-show the next-closest accepting one.
            if (isDiverted(h.id, cat)) continue;
            const d = haversineKm(refLat, refLng, h.lat, h.lng);
            if (d < bestDist) { bestDist = d; bestH = h; }
          }
          if (bestH) catHospitals[cat] = bestH;
        });
        window._catHospitals = catHospitals;
      } else {
        setClosestHospital("N/A");
        window._catHospitals = {};
      }
      setShowHospitalPopup(true);
      setShowDispoPanel(false);
      setShowCallInfo(false);
      setShowUnitHistory(false);
      return;
    }

    if ([97, 98, 99].includes(fk.signal)) {
      setShowStatusPopup(fk.signal);
    } else if (fk.signal) {
      await applyStatus(fk.signal);
    }
  };

  const submitHospitalTransport = async () => {
    if (!logonData || !hospitalCategory) return;
    // Validation: if override is filled, must have a reason
    if (hospitalOverride && !hospitalCloser && !hospitalPatientReq && !hospitalSupv && !hospitalOlmc && !hospitalAltDest) return;

    // Map the typed letter (A-T) back to the category using the same ordering as the popup.
    const defaultCats = ["Adult GED", "Critical Adult", "CRITICAL PEDS", "OBS", "STROKE", "BURN", "STEMI", "LVAD", "SAFE", "PED EDP", "ADULT EDP", "Peds Trauma", "Adult Trauma", "HYPERBARIC", "PEDS GED", "VENOM", "LVO"];
    const letterIdx = hospitalCategory.toUpperCase().charCodeAt(0) - 65;
    const selectedCatName = (letterIdx >= 0 && letterIdx < defaultCats.length) ? defaultCats[letterIdx] : "";
    // Prefer the already-computed closest-per-category hospital (has real hospital_code from Excel).
    const catH = window._catHospitals || {};
    const selectedH = catH[selectedCatName];
    // hospitalOverride (typed hospital code) takes priority; otherwise use the selected category's hospital_code.
    const hospitalCode = hospitalOverride || (selectedH ? selectedH.hospital_code : closestHospital);
    const overrideReason = hospitalCloser ? "CLOSER" : hospitalPatientReq ? "PATIENT REQUEST" : hospitalSupv ? "SUPV" : hospitalOlmc ? "OLMC" : hospitalAltDest ? "ALTERNATE DESTINATION" : "";

    try {
      await axios.put(`${API}/units/${logonData.unit_id}/hospital-transport`, {
        category: selectedCatName || hospitalCategory,
        hospital_id: "",
        hospital_code: hospitalCode,
        hospital_name: "",
        override: overrideReason,
      });
      // Optimistic local state update — don't wait for WS round-trip.
      const ts = getMilTime();
      setCurrentSignal(82);
      setSignalTimestamp(ts);
      setStatusChangedAt(Date.now());
      setSignalTimestamps(prev => ({ ...prev, 82: ts, ...(hospitalChoice ? { [hospitalChoice]: ts } : {}) }));
      setOnCall(true);
      // Store hospital code for status bar display
      setLogonData(prev => ({ ...prev, hospital_code: hospitalCode }));
    } catch (err) {
      console.error("hospital-transport failed", err);
    }
    setShowHospitalPopup(false);
  };

  const selectClass = "h-7 w-full rounded-sm border border-zinc-300 bg-white px-1 text-[11px] text-zinc-600 shadow-sm outline-none";

  // Dedupe any repeated APT/UNIT tokens in the raw address before display.
  // Same logic that `utils.formatAddressShort` uses, inlined here because
  // CrewLoginPage doesn't import from /components/dispatch/utils.
  const dedupeApt = (raw) => {
    if (!raw) return "";
    let s = String(raw)
      .replace(/\b(APT|APARTMENT|UNIT|STE|SUITE|#)\s*([A-Z0-9-]+)(?:\s*,?\s*\1\s*\2\b)+/gi, "$1 $2")
      .replace(/\b((?:APT|APARTMENT|UNIT|STE|SUITE)\s+[A-Z0-9-]+)(?:\s+\1)+\b/gi, "$1");
    return s.trim();
  };
  // Build dynamic status text based on current signal and call data
  // ─── Unit Atom polling ───
  // Look up the unit's current atom (PCT + sector) from its most recent
  // GPS position whenever the unit goes into a transport status (82 or
  // 81). Refreshed every 20s so the atom stays current as the bus moves.
  useEffect(() => {
    if (!loggedIn || !logonData?.unit_id || ![82, 81].includes(currentSignal)) {
      setUnitAtom("");
      return undefined;
    }
    const fetchAtom = async () => {
      const gpsUnit = (window._gpsPositions || []).find((p) => p.mapped_unit_id === logonData.unit_id);
      if (!gpsUnit || gpsUnit.latitude == null) return;
      try {
        const r = await axios.get(`${API}/lookup-atom`, { params: { lat: gpsUnit.latitude, lng: gpsUnit.longitude } });
        setUnitAtom((r.data?.atom || "").trim());
      } catch { /* keep last known */ }
    };
    fetchAtom();
    const t = setInterval(fetchAtom, 20000);
    return () => clearInterval(t);
  }, [loggedIn, logonData?.unit_id, currentSignal]);


  const buildStatusText = () => {
    if (!loggedIn) return "NO STATUS";
    if (!currentSignal) return "NO STATUS";
    const callAddr = abbreviateBorough(dedupeApt(assignedCall?.job?.location || ""));
    const hospCode = assignedCall?.job?.hospital_code || logonData?.hospital_code || "";
    const ts = signalTimestamp || "";
    // unitAtom (state) is the unit's current dispatch atom (PCT+sector)
    // looked up from the latest GPS via /api/lookup-atom — used in the
    // 10-82 / 10-81 status footer.
    switch (currentSignal) {
      case 10: return `DISP ${callAddr} ${ts}`.trim();
      case 63: return `10-63 ${callAddr} ${ts}`.trim();
      case 84: return `10-84 ${callAddr} ${ts}`.trim();
      case 82: return `10-82 ${hospCode}${hospCode ? " > " : ""}${callAddr} ${ts}${unitAtom ? " " + unitAtom : ""}`.trim();
      case 81: {
        const ext = extendedStatus ? ` (${extendedStatus})` : "";
        return `10-81 ${hospCode}${hospCode ? " > " : ""}${callAddr}${ext} ${ts}${unitAtom ? " " + unitAtom : ""}`.trim();
      }
      case 97: return "10-97 AVAILABLE WITHIN RESPONSE AREA";
      case 98: return "10-98 AVAILABLE OUTSIDE RESPONSE AREA";
      case 99: return "10-99 AVAILABLE AT HEADQUARTERS";
      case 89: return "10-89 AVAILABLE AT CROSS STREET LOCATION";
      case 0: return "NOSTAT";
      default: return SIGNAL_LABELS[currentSignal] || `SIGNAL ${currentSignal}`;
    }
  };
  const statusText = buildStatusText();

  return (
    <div className="h-screen w-full bg-black text-white p-0 overflow-hidden" style={{ fontFamily: '"Courier New", monospace' }}>
      <div className="w-full h-full border border-cyan-400 bg-black shadow-2xl flex flex-col">
        {/* Top nav */}
        <div className="flex border-b border-cyan-400 w-full">
          {topTabs.map((tab, i) => {
            const isCallInfoTab = tab.label[0] === "CALL" && tab.label[1] === "INFO";
            const isMciTab = tab.label[0] === "MCI LIST";
            // MCI LIST: yellow when the call code starts with MCI or MAJOR AND the dispatcher
            // has submitted MCI data. Otherwise default grey.
            const callCode = (assignedCall?.call_code || assignedCall?.call_type_full || "").toUpperCase();
            const isMciCall = callCode.startsWith("MCI") || callCode.startsWith("MAJOR");
            const mciActive = isMciTab && isMciCall && !!assignedCall?.job?.mci;
            // Grey when no active call; green when a call is assigned/active; orange when unseen update arrives.
            let callInfoBg = null, callInfoFg = null;
            if (isCallInfoTab) {
              if (hasUnseenInfo) { callInfoBg = "bg-[#FF8800]"; callInfoFg = "text-black animate-pulse"; }
              else if (assignedCall) { callInfoBg = "bg-[#008f00]"; callInfoFg = showCallInfo ? "text-[#1d39ff]" : "text-[#d8ffd8]"; }
              else { callInfoBg = "bg-[#8a8a8a]"; callInfoFg = "text-[#d8d8d8]"; }
            }
            let mciBg = null, mciFg = null;
            if (isMciTab) {
              if (mciActive) { mciBg = "bg-[#FFDD00]"; mciFg = showMciList ? "text-[#1d39ff]" : "text-black"; }
              else { mciBg = "bg-[#8a8a8a]"; mciFg = "text-[#d8d8d8]"; }
            }
            return (
              <div key={i} data-testid={`crew-tab-${tab.label[0].toLowerCase().replace(" ", "")}`}
                onClick={tab.action ? tab.action : tab.label[0] === "LOGON" && !loggedIn && canSubmit ? handleLogon : tab.label[0] === "LOGOFF" ? () => navigate("/") : undefined}
                className={["flex-1", "h-[68px] border-r border-cyan-400 last:border-r-0 flex items-center justify-center text-center text-[16px] leading-4 tracking-tight font-bold cursor-pointer",
                  isCallInfoTab ? `${callInfoBg} ${callInfoFg}` :
                  isMciTab ? `${mciBg} ${mciFg}` :
                  tab.active ? "bg-[#008f00] text-[#d8ffd8]" : "bg-[#8a8a8a] text-[#d8d8d8]",
                  tab.selected ? "text-white" : ""].join(" ")}>
                <div>{tab.label.map((line) => <div key={line}>{line}</div>)}</div>
              </div>
            );
          })}
          <div
            data-testid="crew-1013-btn"
            className="h-[68px] w-[140px] border-l border-cyan-400 flex items-center justify-center text-[24px] font-bold"
            onClick={async () => {
              if (!loggedIn || !logonData?.unit_id) return;
              try {
                await axios.put(`${API}/units/${logonData.unit_id}/emergency`);
              } catch {}
            }}
            style={{
              cursor: loggedIn ? "pointer" : "default",
              background: emergencyActive ? "#FF0000" : "#FFE34C",
              color: emergencyActive ? "#FFF" : "#FFF",
              animation: emergencyActive ? "flash1013 0.6s infinite" : undefined,
            }}
            title={loggedIn ? (emergencyActive ? "Click to clear 10-13 emergency" : "Click to broadcast 10-13 emergency") : ""}
          >
            {emergencyActive ? "10-13" : (selectedUnit ? selectedUnit.name : "10-13")}
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-[128px] border-r border-cyan-400 text-center text-[18px] font-bold text-[#d8d8d8] flex flex-col">
            {/* MESSAGE WAITING — opens a DEDICATED canvas (LEFT pane only)
                showing ONLY new/missed (unread) messages. Per operator spec
                this button must NEVER trigger the right-side message list
                panel — that's the MSG LIST tab's job. Always clickable
                (even with 0 unread) so crew can confirm they're caught up. */}
            <div
              data-testid="crew-message-waiting-btn"
              onClick={() => loggedIn && openOldestUnread()}
              className="flex-1 border-b border-cyan-400 flex items-center justify-center px-2 leading-5 cursor-pointer"
              style={{
                // Per operator spec the MESSAGE WAITING button is steady
                // red with white text whenever there's at least one unread
                // message (no flash). Grey when caught up.
                background: unreadCount > 0 ? "#C00000" : "#8a8a8a",
                color: unreadCount > 0 ? "#FFF" : "#d8d8d8",
              }}
            >
              <div>
                <div>MESSAGE</div>
                <div>WAITING</div>
                <div data-testid="crew-message-waiting-count">{unreadCount}</div>
              </div>
            </div>
            {/* PREV MSG — steps backward one at a time through the unit's
                received-message history. Always grey; only clickable when at
                least one received message exists. */}
            <div
              data-testid="crew-prev-msg-btn"
              onClick={() => loggedIn && goPrevMessage()}
              className="flex-1 border-b border-cyan-400 bg-[#8a8a8a] flex items-center justify-center px-2 leading-5 cursor-pointer"
            >
              <div>
                <div>PREV</div>
                <div>MSG</div>
              </div>
            </div>
            <div className="flex-1 border-b border-cyan-400 bg-[#8a8a8a] flex items-center justify-center px-2 cursor-pointer"
              data-testid="crew-dispo-btn" onClick={() => loggedIn && setShowDispoPanel(!showDispoPanel)}>DISPOSITIONS</div>
            <div
              data-testid="crew-scratchpad-btn"
              className={`flex-1 border-b border-cyan-400 ${showScratchPad ? "bg-[#008f00] text-[#d8ffd8]" : "bg-[#8a8a8a]"} flex items-center justify-center px-2 ${loggedIn ? "cursor-pointer" : ""}`}
              onClick={() => { if (loggedIn) { setShowScratchPad(!showScratchPad); setShowCallInfo(false); setShowUnitHistory(false); setShowDispoPanel(false); setShowMciList(false); setShowHospitalPopup(false); } }}
            >
              SCRATCH PAD
            </div>
            <div className="flex-1 border-b border-cyan-400 bg-[#008f00] text-[#d8ffd8] flex items-center justify-center px-2 cursor-pointer"
              data-testid="crew-xmit-btn"
              onClick={
                showHospitalPopup
                  ? submitHospitalTransport
                  : !loggedIn || forceLogonForm
                    ? (canSubmit
                        ? handleLogon
                        : () => {
                            // Iter 85 — explain WHY the button isn't proceeding
                            // instead of silently doing nothing. Operator was
                            // pressing XMIT and seeing zero feedback whenever a
                            // required field was missing (esp. with MAC binding
                            // hiding the Unit dropdown).
                            const missing = [];
                            if (!agency) missing.push("Agency");
                            if (!unitId) missing.push("Unit");
                            if (!isSupervisor && !boroughCoverage) missing.push("Borough Coverage");
                            if (!crew1) missing.push("Crew 1");
                            if (!radio1) missing.push("Radio 1");
                            if (!isSupervisor && !crew2) missing.push("Crew 2");
                            if (!isSupervisor && !radio2) missing.push("Radio 2");
                            setLogonError(
                              missing.length
                                ? `Cannot transmit — missing required field${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}.`
                                : "Cannot transmit — please review the form."
                            );
                          })
                    : undefined
              }>XMIT</div>
            {/* Yellow duration box */}
            <div className="flex-1 bg-yellow-300 flex items-center justify-center text-[24px] font-bold" style={{ fontFamily: "'VT323', monospace", color: '#CC0000' }}>
              {loggedIn && currentSignal ? getDuration() : "0min"}
            </div>
          </div>

          {/* Center */}
          <div className="flex-1 relative flex flex-col">
            <div className="absolute top-0 left-0 right-32 h-[4px] bg-cyan-400" />

            {/* Command prompt — extracted to components/crew/CommandPrompt.jsx (iter 66). */}
            <CommandPrompt value={commandInput} onChange={setCommandInput} onSubmit={submitCommand} />

            {/* Main black area
                Iter 89 — wrapped in MdtErrorBoundary so any render
                exception (like the iter-81 ReferenceError that caused
                random black screens after call assignment) degrades to a
                readable error + recovery button instead of crashing the
                entire MDT. */}
            <div className="flex-1 relative overflow-auto">
              <MdtErrorBoundary>
              {(!loggedIn || forceLogonForm) ? (
                /* Logon form — extracted to components/crew/LogonForm.jsx (iter 67). */
                <>
                  {/* Iter 83/84 — terminal binding banner is shown ONLY when
                      this terminal is NOT auto-bound. Once a unit is
                      configured (MAC/DEV/IP matched), the banner disappears
                      so the cabin crew sees a clean LOGON screen. The bound
                      unit name is already visible (read-only) in the Unit
                      field of the form. Operators can still override the
                      binding via Admin → Edit Unit → Terminal Identifiers. */}
                  {!emsBindState.loading && !emsBindState.bound && (
                    <>
                      <div data-testid="ems-mdt-bind-banner" style={{ padding: "6px 16px", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "rgba(120, 0, 0, 0.55)", borderBottom: "1px solid rgba(255,255,255,0.2)", color: "#FFF" }}>
                        <span style={{ fontWeight: 700 }}>⚠ Terminal not registered.</span>
                        <span style={{ fontSize: 11, opacity: 0.9 }}>Provide one of these to dispatch to bind this computer to a unit:</span>
                        <span data-testid="ems-mdt-id-mac" style={{ fontFamily: "monospace", fontSize: 11, background: "rgba(0,0,0,0.35)", padding: "2px 6px", borderRadius: 2 }}>MAC: {emsBindState.identifiers?.mac || "—"}</span>
                        <span data-testid="ems-mdt-id-dev" style={{ fontFamily: "monospace", fontSize: 11, background: "rgba(0,0,0,0.35)", padding: "2px 6px", borderRadius: 2 }}>DEV: {(emsBindState.identifiers?.dev || "—").slice(0, 16)}{emsBindState.identifiers?.dev && emsBindState.identifiers.dev.length > 16 ? "…" : ""}</span>
                        <span data-testid="ems-mdt-id-ip" style={{ fontFamily: "monospace", fontSize: 11, background: "rgba(0,0,0,0.35)", padding: "2px 6px", borderRadius: 2 }}>IP: {emsBindState.identifiers?.ip || "—"}</span>
                        <span style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 11 }}>
                          <button
                            data-testid="ems-mdt-set-mac"
                            onClick={() => setEmsBindState((s) => ({ ...s, showMacPrompt: !s.showMacPrompt }))}
                            style={{ background: "transparent", border: "1px solid #FFF", color: "#FFF", padding: "2px 8px", borderRadius: 2, cursor: "pointer" }}
                          >Set MAC</button>
                          <button
                            data-testid="ems-mdt-toggle-manual"
                            onClick={() => setEmsBindState((s) => ({ ...s, showManualOverride: !s.showManualOverride }))}
                            style={{ background: "transparent", border: "1px solid #FFF", color: "#FFF", padding: "2px 8px", borderRadius: 2, cursor: "pointer" }}
                          >Manual override</button>
                        </span>
                      </div>
                      {/* MAC entry sub-banner. */}
                      {emsBindState.showMacPrompt && (
                        <div style={{ padding: "8px 16px", background: "rgba(0,0,0,0.45)", color: "#FFF", display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
                          <span>Enter this computer's MAC address (run <span style={{ fontFamily: "monospace", background: "rgba(255,255,255,0.15)", padding: "1px 4px", borderRadius: 2 }}>ipconfig /all</span> on Windows or <span style={{ fontFamily: "monospace", background: "rgba(255,255,255,0.15)", padding: "1px 4px", borderRadius: 2 }}>ifconfig</span> on Mac/Linux):</span>
                          <input
                            data-testid="ems-mdt-mac-input"
                            placeholder="AA:BB:CC:DD:EE:FF"
                            defaultValue={emsBindState.identifiers?.mac || ""}
                            onKeyDown={(e) => { if (e.key === "Enter") saveTerminalMac(e.currentTarget.value); }}
                            style={{ width: 200, fontFamily: "monospace", padding: "3px 6px", border: "1px solid #888", borderRadius: 2, background: "#FFF", color: "#000" }}
                          />
                          <button
                            data-testid="ems-mdt-mac-save"
                            onClick={(e) => {
                              const inp = e.currentTarget.parentElement.querySelector('[data-testid="ems-mdt-mac-input"]');
                              saveTerminalMac(inp.value);
                            }}
                            style={{ background: "#1E5DA8", border: "none", color: "#FFF", padding: "4px 12px", borderRadius: 2, cursor: "pointer", fontWeight: 700 }}
                          >Save</button>
                        </div>
                      )}
                      {/* Manual override sub-banner — pick a unit explicitly. */}
                      {emsBindState.showManualOverride && (
                        <div style={{ padding: "8px 16px", background: "rgba(0,0,0,0.45)", color: "#FFF", display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
                          <span>Pick the unit to bind this computer to (one-time override):</span>
                          <select
                            data-testid="ems-mdt-manual-unit"
                            defaultValue=""
                            onChange={(e) => manualBindThisTerminal(e.target.value)}
                            style={{ minWidth: 240, padding: "3px 6px", border: "1px solid #888", borderRadius: 2, background: "#FFF", color: "#000" }}
                          >
                            <option value="" style={{ color: "#000", background: "#FFF" }}>— Select unit —</option>
                            {allUnits
                              .filter((u) => !["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF", "LADDER", "RESCUE", "TOWER LADDER"].includes((u.unit_type || "").toUpperCase()))
                              .map((u) => (
                                <option key={u.id} value={u.id} style={{ color: "#000", background: "#FFF" }}>{u.name} — {u.agency}</option>
                              ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  <LogonForm
                    leftFields={leftFields}
                    rightFields={rightFields}
                    unitId={unitId}
                    canSubmit={canSubmit}
                    isSupervisor={isSupervisor}
                    logonError={logonError}
                  />
                </>
              ) : (
                /* Logged in: show SCRATCH PAD, DISPO panel, call info, unit history, MCI, MESSAGES, or black screen */
                showMessages ? (
                  /* MESSAGES canvas — extracted to components/crew/MessagesCanvas.jsx (iter 69). */
                  <MessagesCanvas
                    inboxMessages={inboxMessages}
                    systemMessages={systemMessages}
                    setInboxMessages={setInboxMessages}
                    setSystemMessages={setSystemMessages}
                    selectedMessageId={selectedMessageId}
                    setSelectedMessageId={setSelectedMessageId}
                    showMessages={showMessages}
                    composeMode={composeMode}
                    composeTo={composeTo}
                    setComposeTo={setComposeTo}
                    composeBody={composeBody}
                    setComposeBody={setComposeBody}
                    composeRoster={composeRoster}
                    isSupvForMessaging={isSupvForMessaging}
                    myUnitId={logonData?.unit_id}
                    submitCompose={submitCompose}
                    cancelCompose={cancelCompose}
                    startNewMessage={startNewMessage}
                    startReply={startReply}
                    handleMsgListDelete={handleMsgListDelete}
                  />
                ) : showScratchPad ? (
                  /* SCRATCH PAD — free-type surface. Same font + size as the
                     Call Info screen so crew can jot notes matching the MDT aesthetic. */
                  <textarea
                    data-testid="crew-scratchpad"
                    value={scratchText}
                    onChange={(e) => setScratchText(e.target.value)}
                    placeholder=""
                    className="w-full h-full"
                    style={{
                      background: "#000",
                      color: "#FFFFFF",
                      border: "none",
                      outline: "none",
                      padding: 12,
                      resize: "none",
                      fontFamily: '"Courier New", monospace',
                      fontSize: 18,
                      lineHeight: 1.8,
                      textTransform: "uppercase",
                      caretColor: "#FFFFFF",
                    }}
                  />
                ) : showUnitHistory ? (
                  <div className="flex flex-col h-full overflow-hidden">
                    <div className="flex-1 overflow-auto p-3" style={{ fontFamily: '"Courier New", monospace', fontSize: 15, lineHeight: 1.8, textTransform: "uppercase" }}>
                      <div style={{ color: "#FFF", marginBottom: 12 }}>
                        Unit history for unit {logonData?.unit_name || ""} Displayed at: {logonData?.unit_name || ""}
                      </div>
                      {unitHistory.length === 0 ? (
                        <div style={{ color: "#666" }}>No history entries</div>
                      ) : (
                        unitHistory.map((h) => {
                          const ts = h.timestamp ? new Date(h.timestamp).toLocaleTimeString("en-GB") : "";
                          const dispId = h.dispatcher_id ? ` (${h.dispatcher_id})` : "";
                          return (
                            <div key={h.id} style={{ color: "#FFF", padding: "1px 0" }}>
                              {ts}{dispId} *** {h.details}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : showDispoPanel ? (
                  <div className="flex items-start justify-center h-full pt-4 px-4">
                    <div style={{ maxWidth: 700, width: "100%" }}>
                      <div style={{ background: "linear-gradient(180deg, #3388CC, #2266AA)", padding: "4px 10px", color: "#FFF", fontSize: 14, fontWeight: 700, marginBottom: 4, borderRadius: "2px 2px 0 0" }}>
                        Buttons
                      </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
                      {dispoButtons.map((btn) => {
                        const enabled = btn.requireSignal === currentSignal;
                        return (
                          <button key={btn.code} data-testid={`dispo-btn-${btn.code}`}
                            onClick={() => enabled && handleDispo(btn)}
                            disabled={!enabled}
                            style={{
                              background: enabled ? "linear-gradient(180deg, #E8E8E8, #C8C8C8)" : "#666",
                              border: "2px outset #DDD", borderRadius: 2, padding: "12px 6px",
                              cursor: enabled ? "pointer" : "not-allowed", minHeight: 90, display: "flex", flexDirection: "column",
                              alignItems: "center", justifyContent: "center", textAlign: "center",
                              fontFamily: "'Segoe UI', Tahoma, sans-serif", opacity: enabled ? 1 : 0.4,
                            }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: enabled ? "#000" : "#999", marginBottom: 4 }}>{btn.code}</div>
                            <div style={{ fontSize: 11, color: enabled ? "#333" : "#888", lineHeight: 1.3, whiteSpace: "pre-line" }}>{btn.label}</div>
                          </button>
                        );
                      })}
                      <div /><div /><div /><div />
                      <button data-testid="dispo-close-btn" onClick={() => setShowDispoPanel(false)}
                        style={{
                          background: "linear-gradient(180deg, #E8E8E8, #C8C8C8)",
                          border: "2px outset #DDD", borderRadius: 2, padding: "12px 6px",
                          cursor: "pointer", minHeight: 90, display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", textAlign: "center",
                          fontFamily: "'Segoe UI', Tahoma, sans-serif",
                        }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#000" }}>CLOSE BUTTONS</div>
                      </button>
                    </div>
                    </div>
                  </div>
                ) : showCallInfo && assignedCall ? (
                  /* Call info display - left aligned matching reference.
                     Per operator spec: every event (INITIAL CALL ENTRY, ASSIGNED,
                     ASSIST, dispatcher notes, updates, address changes) is rendered
                     in a single chronological stream sorted by HH:MM:SS. The backend
                     prepends INITIAL CALL ENTRY and appends ASSIGNED / ASSIST rows to
                     job.call_info so the stream is complete without hardcoded rows. */
                  <div className="h-full overflow-auto p-3" style={{ fontFamily: '"Courier New", monospace', fontSize: 18, lineHeight: 1.8, textTransform: "uppercase" }}>
                    <div>
                      <span style={{ color: "#FFF" }}>#{assignedCall.job?.cad_number || "---"}</span>
                      <span style={{ color: "#FFF" }}>{"\u00A0\u00A0\u00A0"}{assignedCall.job?.pct || "NULL"}PCT</span>
                      {assignedCall.job?.sector && (
                        <span style={{ color: "#FFF" }} data-testid="mdt-call-sector">{"\u00A0\u00A0"}{assignedCall.job.sector}</span>
                      )}
                      <span style={{ color: "#00FF00" }}>{"\u00A0\u00A0"}{(() => {
                        // Iter 81 — operator spec: EMS screen header must
                        // show ONLY EMS units assigned to the CAD job.
                        // Fire units/personnel are excluded from this
                        // EMS-side roster line.
                        // Iter 89 — fixed ReferenceError: this code referenced
                        // a non-existent `units` variable. The only loaded
                        // unit list in scope is `allUnits`. The undefined
                        // reference threw at render time, crashing the MDT
                        // to a black screen as soon as a call was assigned
                        // (the MDT was fine until this branch tried to render).
                        const FIRE_TYPES = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF", "LADDER", "RESCUE", "TOWER LADDER"]);
                        const isFireType = (t) => FIRE_TYPES.has((t || "").toUpperCase());
                        const namedUnits = [
                          { name: assignedCall.job?.assigned_unit_name, id: assignedCall.job?.assigned_unit_id },
                          ...((assignedCall.job?.assist_units || []).map((a) => ({ name: a?.unit_name || "", id: a?.unit_id }))),
                        ];
                        return namedUnits
                          .filter((u) => {
                            if (!u.name) return false;
                            const match = (allUnits || []).find((x) => x.id === u.id || x.name === u.name);
                            return !match || !isFireType(match.unit_type);
                          })
                          .map((u) => u.name)
                          .join("  ");
                      })()}</span>
                    </div>
                    <div>
                      <span style={{ color: "#FF0000", fontWeight: 700 }}>{assignedCall.call_code || ""}</span>
                      <span style={{ color: "#FFF" }}>{"\u00A0\u00A0\u00A0\u00A0"}{assignedCall.segment || ""}{"\u00A0\u00A0\u00A0\u00A0"}{assignedCall.call_type_full || ""}</span>
                    </div>
                    <div style={{ color: "#FFFF00", fontWeight: 700 }}>
                      {abbreviateBorough(assignedCall.job?.location || "")}
                    </div>
                    {/* Cross-streets — resolved via OSM Overpass at create/edit
                        and cached. LOW = lower-numbered side of the block,
                        HIGH = higher-numbered side. Special-road labels
                        (DEAD END / PEDESTRIAN PATH / BIKE PATH) surface
                        when the OSM tags indicate. Always renders a line
                        so crews never wonder whether lookup ran. */}
                    <div style={{ color: "#FFF" }} data-testid="mdt-call-crossstreets">
                      X/S:{"\u00A0"}
                      {assignedCall.job?.cross_low || "—"}
                      {"\u00A0"}/{"\u00A0"}
                      {assignedCall.job?.cross_high || "—"}
                      {assignedCall.job?.cross_special && (
                        <span style={{ color: "#FF8800", marginLeft: 10 }} data-testid="mdt-call-cross-special">
                          [{assignedCall.job.cross_special}]
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#FFF" }}>
                      CALLER SOURCE: {assignedCall.job?.caller_source || "N/A"}
                    </div>
                    {/* Location data + call info rendered in a single chronological stream.
                        Merge by timestamp and display in one list so the crew sees
                        everything the dispatcher entered for this call in time order.
                        Per operator spec: only the LATEST "update" entry renders in
                        yellow (yellow = last update). Older updates and the initial
                        ASSIGNED line render in white. "Update" candidates include
                        any subsequent ASSIGNED (primary swap), ASSIST (assist unit
                        assigned), ADDRESS UPDATED, and any dispatcher note. */}
                    <div style={{ marginTop: 12 }}>
                      {(() => {
                        const combined = [
                          ...(assignedCall.job?.location_data || []).map(e => ({ e, kind: "loc" })),
                          ...(assignedCall.job?.call_info || []).map(e => ({ e, kind: "info" })),
                        ];
                        // Sort by the leading HH:MM:SS timestamp if present, else keep insertion order.
                        combined.sort((a, b) => {
                          const ta = (a.e.match(/^\d{2}:\d{2}:\d{2}/) || [""])[0];
                          const tb = (b.e.match(/^\d{2}:\d{2}:\d{2}/) || [""])[0];
                          return ta.localeCompare(tb);
                        });
                        // Identify the index of the LATEST update entry. The
                        // very first ASSIGNED row counts as the initial dispatch
                        // (white). Anything after it that's an ASSIGNED/ASSIST/
                        // ADDRESS UPDATED/dispatcher note is an update — only
                        // the most recent of those gets the yellow color.
                        const isInitialEntry = (e) => /INITIAL CALL ENTRY/i.test(e);
                        let firstAssignedSeen = false;
                        let latestUpdateIdx = -1;
                        combined.forEach(({ e, kind }, i) => {
                          if (kind !== "info") return;
                          if (isInitialEntry(e)) return;
                          const isAssigned = /\sASSIGNED\b/.test(e);
                          const isAssist = /\sASSIST\b/.test(e);
                          const isAddressUpdate = /ADDRESS UPDATED/i.test(e);
                          if (isAssigned && !firstAssignedSeen) {
                            firstAssignedSeen = true;
                            return;  // initial dispatch, not an update
                          }
                          if (isAssigned || isAssist || isAddressUpdate || /\(D\d+\)/i.test(e)) {
                            latestUpdateIdx = i;
                          }
                        });
                        return combined.map(({ e, kind }, i) => (
                          <div key={`${kind}-${i}`} style={{ color: i === latestUpdateIdx ? "#FFDD00" : "#FFF" }}>{e}</div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : showMciList && assignedCall?.job?.mci ? (
                  /* MCI LIST screen — formatted per user's reference image */
                  <div className="h-full overflow-auto p-3" style={{ fontFamily: '"Courier New", monospace', fontSize: 16, lineHeight: 1.6, textTransform: "uppercase", color: "#FFFF00" }}>
                    <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: "#FFF" }}>MCI LIST</div>
                    <div><span style={{ display: "inline-block", minWidth: 140 }}>ADDRESS:</span>{dedupeApt(assignedCall.job.mci.address || assignedCall.job.location || "—")}</div>
                    <div><span style={{ display: "inline-block", minWidth: 140 }}>STAGING LOCATION:</span>{assignedCall.job.mci.staging_location || "—"}</div>
                    <div style={{ marginTop: 10, marginBottom: 6, color: "#FFF", fontWeight: 700 }}>HOSPITALS</div>
                    {(assignedCall.job.mci.categories || []).map((c) => (
                      <div key={c.letter} style={{ display: "flex", gap: 10 }}>
                        <span style={{ minWidth: 30 }}>{c.letter} -</span>
                        <span style={{ minWidth: 180 }}>{(c.category || "").toUpperCase()}</span>
                        <span style={{ minWidth: 60 }}>{c.hospital_code || "---"}</span>
                        <span>{c.abbreviation || ""}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 12 }}>
                      <span style={{ display: "inline-block", minWidth: 140 }}>COMMENTS:</span>{assignedCall.job.mci.comments || "—"}
                    </div>
                  </div>
                ) : showHospitalPopup ? (
                  /* HA/HB Hospital Transport Popup - DOS style.
                     MDT popups do NOT have an X close button — crews must use
                     the proper workflow to dismiss (per operator spec). */
                  <div className="h-full overflow-auto p-3" style={{ fontFamily: '"Courier New", monospace', fontSize: 16, lineHeight: 1.6, textTransform: "uppercase", position: "relative" }}>
                    <div style={{ color: "#FFF", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>CCC</div>
                    {/* Category layout: A-I on left, J-T on right (per user spec) */}
                    <div style={{ display: "flex", gap: 40, marginBottom: 16 }}>
                      {(() => {
                        const allLetters = "ABCDEFGHIJKLMNOPQRST".split("");
                        const defaultCats = ["Adult GED", "Critical Adult", "CRITICAL PEDS", "OBS", "STROKE", "BURN", "STEMI", "LVAD", "SAFE", "PED EDP", "ADULT EDP", "Peds Trauma", "Adult Trauma", "HYPERBARIC", "PEDS GED", "VENOM", "LVO"];
                        const catH = window._catHospitals || {};
                        const renderRow = (letter, idx) => {
                          const cat = defaultCats[idx];
                          if (!cat) {
                            return (
                              <div key={letter} style={{ color: "#555", display: "flex" }}>
                                <span style={{ minWidth: 30 }}>{letter} -</span>
                                <span style={{ minWidth: 180 }}>---</span>
                              </div>
                            );
                          }
                          const h = catH[cat] || hospitals.find(hosp => hosp.capabilities && hosp.capabilities.includes(cat));
                          return (
                            <div key={letter} style={{ color: "#FFF", display: "flex" }}>
                              <span style={{ minWidth: 30 }}>{letter} -</span>
                              <span style={{ minWidth: 180 }}>{cat}</span>
                              <span style={{ minWidth: 50 }}>{h?.hospital_code || "---"}</span>
                              <span>{h?.abbreviation || ""}</span>
                            </div>
                          );
                        };
                        const leftLetters = allLetters.slice(0, 9);   // A-I
                        const rightLetters = allLetters.slice(9);     // J-T
                        return (
                          <>
                            <div style={{ flex: 1 }}>
                              {leftLetters.map((L, i) => renderRow(L, i))}
                            </div>
                            <div style={{ flex: 1 }}>
                              {rightLetters.map((L, i) => renderRow(L, i + 9))}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    <div style={{ color: "#FFF", marginBottom: 8 }}>
                      Closest hospital : <span style={{ fontWeight: 700 }}>{closestHospital || "N/A"}</span>
                    </div>
                    <div style={{ color: "#FFF", display: "flex", alignItems: "center", marginBottom: 8 }}>
                      Select hospital & category A-T{"\u00A0"}
                      <input data-testid="hospital-category" value={hospitalCategory} onChange={(e) => setHospitalCategory(e.target.value.toUpperCase().slice(0, 1))}
                        maxLength={1} style={{ width: 30, height: 24, background: "#444", color: "#FFF", border: "1px solid #888", textAlign: "center", fontSize: 16, fontFamily: '"Courier New", monospace' }} />
                    </div>
                    <div style={{ color: "#FFF", display: "flex", alignItems: "center", marginBottom: 12 }}>
                      Override suggestion to hospital #{"\u00A0"}
                      <input data-testid="hospital-override" value={hospitalOverride} onChange={(e) => setHospitalOverride(e.target.value.toUpperCase().slice(0, 3))}
                        maxLength={3} style={{ width: 50, height: 24, background: "#444", color: "#0F0", border: "1px solid #888", textAlign: "center", fontSize: 16, fontFamily: '"Courier New", monospace' }} />
                    </div>
                    <div style={{ color: "#FFF", display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                      {[
                        { label: "Closer", val: hospitalCloser, set: setHospitalCloser },
                        { label: "Patient Requests", val: hospitalPatientReq, set: setHospitalPatientReq },
                        { label: "supv", val: hospitalSupv, set: setHospitalSupv },
                        { label: "OLMC", val: hospitalOlmc, set: setHospitalOlmc },
                        { label: "Alternate Destination", val: hospitalAltDest, set: setHospitalAltDest },
                      ].map((f) => (
                        <span key={f.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {f.label}
                          <input value={f.val} onChange={(e) => { const v = e.target.value.toUpperCase(); f.set(v === "X" ? "X" : ""); }}
                            maxLength={1} style={{ width: 24, height: 24, background: "#444", color: "#FFF", border: "1px solid #888", textAlign: "center", fontSize: 14, fontFamily: '"Courier New", monospace' }} />
                        </span>
                      ))}
                    </div>
                    {hospitalOverride && !hospitalCloser && !hospitalPatientReq && !hospitalSupv && !hospitalOlmc && !hospitalAltDest && (
                      <div style={{ color: "#FF4444", fontSize: 14, marginBottom: 8 }}>Override requires a reason (Closer, Patient Requests, supv, OLMC, or Alternate Destination)</div>
                    )}
                  </div>
                ) : <div className="flex-1" />
              )}

              {/* ─── MDT Popups (extracted to components/crew/MdtPopups.js).
                  None have X close buttons per operator spec — crews must
                  follow the workflow (click / YES / PRESS TO GO). ─── */}
              <UnitLoggedOnPopup
                show={showLoggedInPopup}
                onAcknowledge={() => setShowLoggedInPopup(false)} />
              <CallAssignedPopup
                show={showCallAssignedPopup}
                onAcknowledge={() => {
                  setShowCallAssignedPopup(false);
                  // Force a clean switch to CALL INFO — close everything
                  // else so the underlying canvas isn't displaying stale
                  // panels (e.g. an HA/HB screen left over from before).
                  setShowCallInfo(true);
                  setShowMessages(false);
                  setShowMciList(false);
                  setShowUnitHistory(false);
                  setShowDispoPanel(false);
                  setShowHospitalPopup(false);
                  setShowScratchPad(false);
                  setComposeMode(null);
                }} />
              <UpdatePopup
                show={!!addressUpdate}
                onAcknowledge={() => { setAddressUpdate(null); setShowCallInfo(true); setShowDispoPanel(false); setShowUnitHistory(false); }} />
              <StatusConfirmPopup
                signal={showStatusPopup}
                onConfirm={confirmStatus}
                onCancel={() => setShowStatusPopup(null)} />
              <LogoffConfirmPopup
                show={showLogoffConfirm}
                onYes={confirmLogoff}
                onNo={() => setShowLogoffConfirm(false)} />

              {/* The MESSAGE WAITING / MSG LIST workflow renders directly
                  inside the MDT canvas (see the showMessages branch in the
                  main canvas conditional above). No popup / modal — per
                  operator spec the message must appear on-screen formatted
                  exactly like the reference photos.  */}
              </MdtErrorBoundary>
            </div>

            {/* Status bar at bottom */}
            <div className="mt-auto border-t border-cyan-400">
              <div className="px-0 pt-1 text-[18px] font-black tracking-tight">
                <span className={`ml-2 ${currentSignal ? "text-white" : "text-red-500"}`}>{statusText}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-cyan-400 bg-black text-[16px] font-bold text-[#d8d8d8]">
          <div className="flex items-center">
            <div className="flex-1 px-1 py-1" data-testid="crew-footer-msg">
              {bottomMessage || "no more messages"}
            </div>
            <div className="min-w-[3rem] h-7 border-l border-r border-cyan-400 last:border-r-0 bg-black flex items-center px-1">
              {mdtGpsUnit && <GpsShareIndicator state={mdtGps} inline />}
            </div>
            <div className="w-12 h-7 border-r border-cyan-400 last:border-r-0 bg-[#008f00] text-white flex items-center justify-center">G</div>
            <div className="w-[240px] h-7 bg-[#1d39ff] flex items-center px-4 text-[18px] whitespace-nowrap" style={{ color: "#FFFFFF" }}>{dateStr}</div>
          </div>
        </div>

        {/* Function keys — extracted to components/crew/FunctionKeys.jsx (iter 68). */}
        <FunctionKeys
          functionKeys={functionKeys}
          loggedIn={loggedIn}
          currentSignal={currentSignal}
          hospitalChoice={hospitalChoice}
          onCall={onCall}
          assignedCall={assignedCall}
          signalTimestamps={signalTimestamps}
          signalTimestamp={signalTimestamp}
          canCrewChangeSignal={canCrewChangeSignal}
          onPress={handleFnKey}
        />
      </div>
    </div>
  );
}
